import { useState, useEffect } from "react";
import { useAuth, API, isImpersonating } from "@/App";
import { useBranding } from "@/contexts/BrandingContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Settings,
  Users,
  Palette,
  Save,
  RefreshCw,
  Upload,
  Link2,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Building,
  UserPlus,
  Ban,
  Trash2,
  UserCheck,
  FileText,
  BarChart3,
  Linkedin,
  Target,
  TrendingUp,
  AlertCircle,
  Database,
  Search,
} from "lucide-react";

// Emails that can change instance name
const INSTANCE_NAME_ADMINS = ["jason@jci-marketing.com", "kevin@jci-marketing.com"];
// JCI domain for Notes tab visibility
const JCI_DOMAIN = "jci-marketing.com";
// MUST match backend config.py MASTER_ADMIN_EMAILS
const MASTER_ADMIN_EMAILS = ["jason@jci-marketing.com", "kevin@jci-marketing.com"];

const AdminPanelPage = () => {
  const { sessionToken, isMasterAdmin, user } = useAuth();
  const { refreshBranding } = useBranding();
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [linkedInOrgs, setLinkedInOrgs] = useState([]);
  const [selectedLinkedInOrg, setSelectedLinkedInOrg] = useState(null);
  const [selectingOrg, setSelectingOrg] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connectingHubSpot, setConnectingHubSpot] = useState(false);
  const [connectingLinkedIn, setConnectingLinkedIn] = useState(false);
  const [connectingLinkedInAds, setConnectingLinkedInAds] = useState(false);
  const [connectingGoogleAds, setConnectingGoogleAds] = useState(false);
  const [connectingGA4, setConnectingGA4] = useState(false);
  const [connectingGSC, setConnectingGSC] = useState(false);
  const [uploadingVisits, setUploadingVisits] = useState(false);
  const [websiteVisitsStats, setWebsiteVisitsStats] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Track current tenant to refetch settings when it changes
  const [currentViewingTenant, setCurrentViewingTenant] = useState(() => sessionStorage.getItem("viewing_tenant_id"));
  
  // HubSpot Sync
  const [syncingHubSpot, setSyncingHubSpot] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  
  // LinkedIn Follower History Sync
  const [syncingLinkedInFollowers, setSyncingLinkedInFollowers] = useState(false);
  
  // User management
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [newUserData, setNewUserData] = useState({ name: "", email: "", role: "user", password: "" });
  const [addingUser, setAddingUser] = useState(false);
  
  // Notes tab
  const [devNotes, setDevNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  
  // Goals tab
  const [goals, setGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);
  const [showAddGoalDialog, setShowAddGoalDialog] = useState(false);
  const [newGoalData, setNewGoalData] = useState({ metric: "mqls", target_value: "", period: "monthly" });
  
  // Webhook & Competitor settings (JCI only)
  const [webhookSettings, setWebhookSettings] = useState({
    webhook_url: "",
    competitors: ["", "", ""]
  });
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [loadingWebhook, setLoadingWebhook] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState(null);
  const [loadingSecret, setLoadingSecret] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  
  // Connection permissions (LinkedIn lock when another user connected)
  const [connectionPermissions, setConnectionPermissions] = useState({});
  
  // Google Ads CSV upload state
  const [uploadingGoogleAdsCampaigns, setUploadingGoogleAdsCampaigns] = useState(false);
  const [uploadingGoogleAdsKeywords, setUploadingGoogleAdsKeywords] = useState(false);
  const [googleAdsUploadStats, setGoogleAdsUploadStats] = useState(null);
  const [googleAdsUploadHistory, setGoogleAdsUploadHistory] = useState(null);
  
  // Google Ads Targeted Keywords state
  const [targetedKeywordsCampaign, setTargetedKeywordsCampaign] = useState('');
  const [targetedKeywordsCustomCampaign, setTargetedKeywordsCustomCampaign] = useState('');
  const [targetedKeywordsText, setTargetedKeywordsText] = useState('');
  const [uploadingTargetedKeywords, setUploadingTargetedKeywords] = useState(false);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [existingTargetedKeywords, setExistingTargetedKeywords] = useState(null);
  
  // Feature settings (what features are enabled for this tenant)
  const [featureSettings, setFeatureSettings] = useState({
    show_google_ads_upload: false
  });
  
  // Tenant display settings (country exclusions, traffic data source, etc.)
  const [displaySettings, setDisplaySettings] = useState({
    exclude_turkey_traffic: false,
    excluded_countries: [],
    traffic_data_source: "ga4",  // "ga4" or "hubspot"
    show_page_views_chart: true,
    show_mapped_stages_indicator: true,
    show_true_ai_referrals: true,  // Toggle for True AI Referrals card
    show_turkey_exclusion_alert: true  // Toggle for Turkey exclusion alert visibility
  });
  const [savingDisplaySettings, setSavingDisplaySettings] = useState(false);
  
  // AI Traffic tracking state
  const [aiTrafficData, setAiTrafficData] = useState({
    true_ai_referrals: 0,
    period_label: "",
    last_updated: null,
    updated_by: null,
    notes: "",
    monthly_data: []
  });
  const [savingAiTraffic, setSavingAiTraffic] = useState(false);
  
  // HubSpot lifecycle stages
  const [lifecycleStages, setLifecycleStages] = useState([]);
  const [contactsSummary, setContactsSummary] = useState(null);
  const [loadingStages, setLoadingStages] = useState(false);
  
  // HubSpot deal stages and probabilities
  const [dealStages, setDealStages] = useState([]);
  const [dealStageProbabilities, setDealStageProbabilities] = useState({});
  const [loadingDealStages, setLoadingDealStages] = useState(false);
  const [savingProbabilities, setSavingProbabilities] = useState(false);
  
  // Raw text for stage mapping textareas (allows typing commas/newlines)
  const [mqlStagesText, setMqlStagesText] = useState('');
  const [sqlStagesText, setSqlStagesText] = useState('');
  const [closedWonStagesText, setClosedWonStagesText] = useState('');
  
  // ICP criteria for different pages
  const [dealsIcpCriteria, setDealsIcpCriteria] = useState('');

  // Check if current user can change instance name
  const canChangeInstanceName = user && INSTANCE_NAME_ADMINS.includes(user.email);
  // Check if current user can see Notes tab (SUPER ADMINS ONLY - not visible when impersonating tenants)
  const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
  // No hardcoded tenant ID - we check if user is viewing their own tenant via branding context
  // Notes are visible only when NOT impersonating another tenant
  const [homeTenantId, setHomeTenantId] = useState(null);
  const isViewingOwnTenant = !viewingTenantId || (homeTenantId && viewingTenantId === homeTenantId);
  const canSeeNotes = user && MASTER_ADMIN_EMAILS.includes(user.email) && isViewingOwnTenant;
  
  // Fetch home tenant dynamically
  useEffect(() => {
    const fetchHomeTenant = async () => {
      if (!sessionToken) return;
      try {
        const response = await axios.get(`${API}/tenants/default-home`, {
          params: { Authorization: `Bearer ${sessionToken}` }
        });
        if (response.data?.tenant_id) {
          setHomeTenantId(response.data.tenant_id);
        }
      } catch (err) {
        console.error("Failed to fetch home tenant:", err);
      }
    };
    if (MASTER_ADMIN_EMAILS.includes(user?.email)) {
      fetchHomeTenant();
    }
  }, [sessionToken, user?.email]);
  
  // Only JCI super admins can see refresh/sync buttons on integrations
  const canSeeRefreshButtons = user && MASTER_ADMIN_EMAILS.includes(user.email);
  
  // Check if current user is a super admin OR is impersonating (super admins can impersonate)
  // When impersonating, the original admin retains their super admin privileges for admin functions
  const impersonating = isImpersonating();
  const isSuperAdmin = (user && MASTER_ADMIN_EMAILS.includes(user.email)) || impersonating;

  // Check if user can connect an integration
  const checkConnectionPermission = async (integration) => {
    try {
      const tenantId = sessionStorage.getItem("viewing_tenant_id") || "default";
      const response = await axios.get(`${API}/tenants/${tenantId}/can-connect/${integration}`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setConnectionPermissions(prev => ({
        ...prev,
        [integration]: response.data
      }));
      return response.data.can_connect;
    } catch (error) {
      console.error(`Error checking ${integration} permission:`, error);
      return true; // Default to allowing connection on error
    }
  };

  const fetchUsers = async () => {
    try {
      // If viewing a specific tenant, fetch that tenant's users
      const tenantId = sessionStorage.getItem("viewing_tenant_id");
      
      if (tenantId && !isSuperAdmin) {
        // Regular admin viewing their tenant - fetch tenant users
        const response = await axios.get(`${API}/tenants/${tenantId}/users`, {
          params: { Authorization: `Bearer ${sessionToken}` },
        });
        // Transform tenant membership data to user format
        const tenantUsers = response.data.map(m => ({
          user_id: m.user_id || m.membership_id,
          email: m.email,
          name: m.name || m.email.split('@')[0],
          role: m.role,
          is_active: true,
          created_at: m.created_at
        }));
        setUsers(tenantUsers);
      } else if (tenantId && isSuperAdmin) {
        // Super admin viewing a tenant - also fetch tenant users
        const response = await axios.get(`${API}/tenants/${tenantId}/users`, {
          params: { Authorization: `Bearer ${sessionToken}` },
        });
        const tenantUsers = response.data.map(m => ({
          user_id: m.user_id || m.membership_id,
          email: m.email,
          name: m.name || m.email.split('@')[0],
          role: m.role,
          is_active: true,
          created_at: m.created_at
        }));
        setUsers(tenantUsers);
      } else {
        // No tenant context (super admin home) - fetch all users
        const response = await axios.get(`${API}/users`, {
          params: { Authorization: `Bearer ${sessionToken}` },
        });
        setUsers(response.data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchGoals = async () => {
    setLoadingGoals(true);
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (!viewingTenantId) {
        setGoals([]);
        return;
      }
      const response = await axios.get(
        `${API}/ai-analytics/goals?X-Tenant-ID=${viewingTenantId}&Authorization=${sessionToken}`
      );
      setGoals(response.data.goals || []);
    } catch (error) {
      console.error("Error fetching goals:", error);
      setGoals([]);
    } finally {
      setLoadingGoals(false);
    }
  };

  const fetchSettings = async () => {
    try {
      // Get current tenant context
      const tenantId = sessionStorage.getItem("viewing_tenant_id");
      
      // First fetch global admin settings
      const response = await axios.get(`${API}/admin/settings`, {
        params: { Authorization: `Bearer ${sessionToken}` },
      });
      let settingsData = response.data;
      
      // If we have a tenant context, fetch tenant-specific branding and settings
      if (tenantId) {
        try {
          const brandingResponse = await axios.get(`${API}/tenants/${tenantId}/branding`, {
            params: { Authorization: `Bearer ${sessionToken}` },
          });
          
          // Merge tenant branding into settings
          console.log("Loaded tenant branding:", brandingResponse.data);
          settingsData = {
            ...settingsData,
            company_logo: brandingResponse.data.logo_url,
            company_name: brandingResponse.data.tenant_name,
            default_brand_colors: {
              primary: brandingResponse.data.primary_color || "#e066ff",
              secondary: brandingResponse.data.secondary_color || "#0F172A",
              chart_colors: brandingResponse.data.chart_colors || ["#e066ff", "#0F172A", "#0EA5E9", "#64748B", "#10B981"],
            }
          };
        } catch (brandingError) {
          console.log("No tenant-specific branding, using defaults");
        }
        
        // Also fetch tenant's stage mapping and settings
        try {
          const tenantResponse = await axios.get(`${API}/tenants/${tenantId}`, {
            params: { Authorization: `Bearer ${sessionToken}` },
          });
          if (tenantResponse.data?.hubspot_stage_mapping) {
            settingsData.hubspot_stage_mapping = tenantResponse.data.hubspot_stage_mapping;
            console.log("Loaded stage mapping:", tenantResponse.data.hubspot_stage_mapping);
            // Initialize raw text states for textareas
            const mqlStages = tenantResponse.data.hubspot_stage_mapping.mql_stages || [];
            const sqlStages = tenantResponse.data.hubspot_stage_mapping.sql_stages || [];
            const closedWonStages = tenantResponse.data.hubspot_stage_mapping.closed_won_stages || [];
            setMqlStagesText(mqlStages.join(', '));
            setSqlStagesText(sqlStages.join(', '));
            setClosedWonStagesText(closedWonStages.join(', '));
          }
          // Load Deals ICP criteria
          if (tenantResponse.data?.deals_icp_criteria) {
            setDealsIcpCriteria(tenantResponse.data.deals_icp_criteria);
          }
          // Load social insights visibility settings
          if (tenantResponse.data?.settings) {
            settingsData.social_insights = {
              show_page_views_chart: tenantResponse.data.settings.show_page_views_chart ?? true,
              show_engagement_chart: tenantResponse.data.settings.show_engagement_chart ?? true,
              show_page_views_table: tenantResponse.data.settings.show_page_views_table ?? true,
              show_additional_metrics: tenantResponse.data.settings.show_additional_metrics ?? true
            };
            console.log("Loaded social insights settings:", settingsData.social_insights);
          }
        } catch (tenantError) {
          console.log("Could not fetch tenant stage mapping");
        }
        
        // Fetch feature settings for this tenant
        try {
          const featureResponse = await axios.get(`${API}/tenants/${tenantId}/feature-settings`, {
            params: { Authorization: `Bearer ${sessionToken}` },
          });
          setFeatureSettings(featureResponse.data);
          console.log("Loaded feature settings:", featureResponse.data);
        } catch (featureError) {
          console.log("Could not fetch feature settings, using defaults");
          setFeatureSettings({ show_google_ads_upload: false });
        }
      }
      
      setSettings(settingsData);
    } catch (error) {
      console.error("Error fetching settings:", error);
      setSettings({
        platform_name: "JCI Analytics",
        company_name: "",
        default_brand_colors: {
          primary: "#00A5A8",
          secondary: "#0F172A",
          chart_colors: ["#00A5A8", "#0F172A", "#0EA5E9", "#64748B", "#10B981"],
        },
        features: {
          ai_chat: true,
          icp_matching: true,
          pdf_export: true,
          pptx_export: true,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchIntegrationStatus = async () => {
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      const response = await axios.get(`${API}/integrations/status`, { params });
      setIntegrationStatus(response.data);
      
      // Also fetch sync status for super admins
      if (user?.role === 'super_admin') {
        fetchSyncStatus();
      }
    } catch (error) {
      console.error("Error fetching integration status:", error);
      setIntegrationStatus({
        hubspot: { connected: false },
        linkedin: { connected: false },
        ga4: { connected: false },
      });
    }
  };

  const fetchGoogleAdsUploadHistory = async () => {
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      const response = await axios.get(`${API}/integrations/google-ads/upload-history`, { params });
      setGoogleAdsUploadHistory(response.data);
    } catch (error) {
      console.error("Error fetching Google Ads upload history:", error);
      setGoogleAdsUploadHistory(null);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchSettings();
    fetchIntegrationStatus();
    fetchGoals();
    // Check connection permissions for LinkedIn integrations
    checkConnectionPermission("linkedin");
    checkConnectionPermission("linkedin_ads");
    // Fetch HubSpot lifecycle stages and contacts summary
    fetchLifecycleStages();
    // Fetch HubSpot deal stages for probability settings
    fetchDealStages();
    // Fetch display settings for current tenant (super admin only)
    fetchDisplaySettings();
    // Fetch AI traffic data
    fetchAiTrafficData();
    // Fetch Google Ads upload history
    fetchGoogleAdsUploadHistory();
    // Load active campaigns and existing targeted keywords
    loadActiveCampaigns();
    loadExistingTargetedKeywords();
  }, [sessionToken, currentViewingTenant]);

  // Listen for tenant changes (when switching between tenants)
  useEffect(() => {
    const checkTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (newTenantId !== currentViewingTenant) {
        console.log("Tenant changed from", currentViewingTenant, "to", newTenantId);
        setCurrentViewingTenant(newTenantId);
        // Reset stage mapping text fields to trigger refetch
        setMqlStagesText('');
        setSqlStagesText('');
        setClosedWonStagesText('');
      }
    };
    
    // Check periodically (sessionStorage doesn't have events)
    const interval = setInterval(checkTenantChange, 500);
    
    // Also check on focus (when user switches back to this tab)
    window.addEventListener('focus', checkTenantChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', checkTenantChange);
    };
  }, [currentViewingTenant]);

  // Fetch AI traffic data for current tenant
  const fetchAiTrafficData = async () => {
    try {
      const tenantId = sessionStorage.getItem("viewing_tenant_id");
      if (!tenantId) return;
      
      const response = await axios.get(`${API}/tenants/${tenantId}/display-settings`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      const aiData = response.data?.ai_referral_tracking || {
        true_ai_referrals: 0,
        period_label: "",
        last_updated: null,
        updated_by: null,
        notes: "",
        monthly_data: []
      };
      setAiTrafficData(aiData);
    } catch (error) {
      console.error("Error fetching AI traffic data:", error);
    }
  };

  // Save AI traffic data
  const saveAiTrafficData = async () => {
    setSavingAiTraffic(true);
    try {
      const tenantId = sessionStorage.getItem("viewing_tenant_id");
      if (!tenantId) {
        toast.error("No tenant selected");
        return;
      }
      
      const updatedData = {
        ...aiTrafficData,
        last_updated: new Date().toISOString(),
        updated_by: user?.email || "admin"
      };
      
      await axios.put(`${API}/tenants/${tenantId}/display-settings`, 
        { ai_referral_tracking: updatedData },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      setAiTrafficData(updatedData);
      toast.success("AI referral data saved");
    } catch (error) {
      console.error("Error saving AI traffic data:", error);
      toast.error("Failed to save AI traffic data");
    } finally {
      setSavingAiTraffic(false);
    }
  };

  // Fetch display settings for current tenant
  const fetchDisplaySettings = async () => {
    try {
      const tenantId = sessionStorage.getItem("viewing_tenant_id");
      if (!tenantId) return;
      
      const response = await axios.get(`${API}/tenants/${tenantId}/display-settings`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setDisplaySettings(response.data);
    } catch (error) {
      console.error("Error fetching display settings:", error);
    }
  };

  // Save display settings
  const saveDisplaySettings = async (newSettings) => {
    setSavingDisplaySettings(true);
    try {
      const tenantId = sessionStorage.getItem("viewing_tenant_id");
      if (!tenantId) {
        toast.error("No tenant selected");
        return;
      }
      
      await axios.put(`${API}/tenants/${tenantId}/display-settings`, newSettings, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setDisplaySettings(newSettings);
      toast.success("Display settings saved");
    } catch (error) {
      console.error("Error saving display settings:", error);
      toast.error("Failed to save display settings");
    } finally {
      setSavingDisplaySettings(false);
    }
  };

  // Toggle Turkey traffic exclusion
  const toggleTurkeyTraffic = () => {
    const newSettings = {
      ...displaySettings,
      exclude_turkey_traffic: !displaySettings.exclude_turkey_traffic
    };
    saveDisplaySettings(newSettings);
  };

  // Fetch HubSpot lifecycle stages
  const fetchLifecycleStages = async () => {
    setLoadingStages(true);
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      const [stagesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/integrations/hubspot/lifecycle-stages`, { params }),
        axios.get(`${API}/integrations/hubspot/contacts-summary`, { params })
      ]);
      
      // Handle both formats: array of strings OR array of {label, value} objects
      const rawStages = stagesRes.data?.stages || [];
      const processedStages = rawStages.map(stage => {
        // If it's already an object with label/value, keep it
        if (stage && typeof stage === 'object' && stage.label) {
          return { label: stage.label, value: stage.value || stage.label };
        }
        // If it's a string, convert to object format
        if (typeof stage === 'string') {
          return { label: stage, value: stage };
        }
        return null;
      }).filter(Boolean);
      
      setLifecycleStages(processedStages);
      setContactsSummary(summaryRes.data);
    } catch (error) {
      console.error("Error fetching lifecycle stages:", error);
    } finally {
      setLoadingStages(false);
    }
  };

  // Fetch HubSpot deal stages and probabilities
  const fetchDealStages = async () => {
    setLoadingDealStages(true);
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      
      // Fetch deal stages from cached data
      const stagesRes = await axios.get(`${API}/integrations/hubspot/deal-stages`, { params });
      const rawStages = stagesRes.data?.stages || [];
      
      // Handle both array of objects and dict formats
      let stagesArray = [];
      if (Array.isArray(rawStages)) {
        // Array format: [{label, value, pipeline}, ...]
        stagesArray = rawStages.map(stage => ({
          id: stage.value,
          label: stage.label,
          pipeline: stage.pipeline
        }));
      } else if (typeof rawStages === 'object') {
        // Dict format: {id: label, ...}
        stagesArray = Object.entries(rawStages).map(([id, label]) => ({
          id,
          label,
        }));
      }
      
      // Sort by label for better UX
      stagesArray.sort((a, b) => a.label.localeCompare(b.label));
      setDealStages(stagesArray);
      
      // Fetch existing probabilities for this tenant
      if (viewingTenantId) {
        try {
          const probRes = await axios.get(`${API}/tenants/${viewingTenantId}/deal-stage-probabilities`, { params });
          setDealStageProbabilities(probRes.data?.probabilities || {});
        } catch (e) {
          console.log("No existing probabilities found");
          setDealStageProbabilities({});
        }
      }
    } catch (error) {
      console.error("Error fetching deal stages:", error);
      // Don't show error toast - deal stages may not be synced yet
    } finally {
      setLoadingDealStages(false);
    }
  };

  // Save deal stage probabilities
  const saveDealStageProbabilities = async () => {
    setSavingProbabilities(true);
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (!viewingTenantId) {
        toast.error("No tenant selected");
        return;
      }
      
      const params = { Authorization: `Bearer ${sessionToken}` };
      await axios.put(
        `${API}/tenants/${viewingTenantId}/deal-stage-probabilities`,
        { probabilities: dealStageProbabilities },
        { params }
      );
      
      toast.success("Deal stage probabilities saved!");
    } catch (error) {
      console.error("Error saving probabilities:", error);
      toast.error("Failed to save probabilities");
    } finally {
      setSavingProbabilities(false);
    }
  };

  // Update a single deal stage probability
  const updateProbability = (stageId, value) => {
    const numValue = value === '' ? '' : Math.min(100, Math.max(0, parseInt(value) || 0));
    setDealStageProbabilities(prev => ({
      ...prev,
      [stageId]: numValue
    }));
  };

  // Handle Google Ads Campaign CSV Upload
  const handleGoogleAdsCampaignUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingGoogleAdsCampaigns(true);
    const formData = new FormData();
    formData.append("file", file);

    // Get viewing tenant ID for super admin context
    const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
    console.log("Google Ads Campaign Upload - viewingTenantId:", viewingTenantId);

    try {
      // Don't manually set Content-Type for FormData - axios handles it automatically
      const headers = {};
      if (viewingTenantId) {
        headers["X-Tenant-ID"] = viewingTenantId;
      }
      
      const response = await axios.post(`${API}/integrations/google-ads/upload/campaigns`, formData, {
        params: { Authorization: `Bearer ${sessionToken}` },
        headers
      });
      toast.success(`Uploaded ${response.data.records} campaign records for tenant ${response.data.tenant_id?.substring(0, 8) || ''}`);
      setGoogleAdsUploadStats(prev => ({
        ...prev,
        campaigns: response.data
      }));
      // Refresh upload history
      fetchGoogleAdsUploadHistory();
    } catch (error) {
      toast.error("Failed to upload campaign data: " + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingGoogleAdsCampaigns(false);
      e.target.value = "";
    }
  };

  // Handle Google Ads Keywords CSV Upload
  const handleGoogleAdsKeywordUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingGoogleAdsKeywords(true);
    const formData = new FormData();
    formData.append("file", file);

    // Get viewing tenant ID for super admin context
    const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
    console.log("Google Ads Keyword Upload - viewingTenantId:", viewingTenantId);

    try {
      // Don't manually set Content-Type for FormData - axios handles it automatically
      const headers = {};
      if (viewingTenantId) {
        headers["X-Tenant-ID"] = viewingTenantId;
      }
      
      const response = await axios.post(`${API}/integrations/google-ads/upload/keywords`, formData, {
        params: { Authorization: `Bearer ${sessionToken}` },
        headers
      });
      toast.success(`Uploaded ${response.data.records} keyword records`);
      setGoogleAdsUploadStats(prev => ({
        ...prev,
        keywords: response.data
      }));
      // Refresh upload history
      fetchGoogleAdsUploadHistory();
    } catch (error) {
      toast.error("Failed to upload keyword data: " + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingGoogleAdsKeywords(false);
      e.target.value = "";
    }
  };

  // Load active campaigns for dropdown
  const loadActiveCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      const response = await axios.get(`${API}/integrations/google-ads/campaigns/active`, { params });
      setActiveCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error("Failed to load campaigns:", error);
      toast.error("Failed to load campaigns");
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // Load existing targeted keywords
  const loadExistingTargetedKeywords = async () => {
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      const response = await axios.get(`${API}/integrations/google-ads/targeted-keywords`, { params });
      setExistingTargetedKeywords(response.data);
    } catch (error) {
      console.error("Failed to load targeted keywords:", error);
    }
  };

  // Handle targeted keywords upload
  const handleTargetedKeywordsUpload = async () => {
    if (!targetedKeywordsText.trim()) {
      toast.error("Please enter keywords to upload");
      return;
    }
    
    const campaignName = targetedKeywordsCampaign === '__custom__' 
      ? targetedKeywordsCustomCampaign.trim()
      : targetedKeywordsCampaign;
      
    if (!campaignName) {
      toast.error("Please select or enter a campaign name");
      return;
    }

    setUploadingTargetedKeywords(true);
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const headers = { "Content-Type": "application/json" };
      if (viewingTenantId) {
        headers["X-Tenant-ID"] = viewingTenantId;
      }
      
      const response = await axios.post(
        `${API}/integrations/google-ads/targeted-keywords`,
        {
          keywords: targetedKeywordsText,
          campaign_name: targetedKeywordsCampaign === '__custom__' ? targetedKeywordsCustomCampaign : null,
          campaign_id: targetedKeywordsCampaign !== '__custom__' ? targetedKeywordsCampaign : null
        },
        {
          params: { Authorization: `Bearer ${sessionToken}` },
          headers
        }
      );
      
      toast.success(`Uploaded ${response.data.keywords_count} keywords for "${response.data.campaign}"`);
      
      // Clear form
      setTargetedKeywordsText('');
      setTargetedKeywordsCampaign('');
      setTargetedKeywordsCustomCampaign('');
      
      // Refresh existing keywords
      loadExistingTargetedKeywords();
    } catch (error) {
      toast.error("Failed to upload targeted keywords: " + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingTargetedKeywords(false);
    }
  };

  // Delete targeted keywords for a campaign
  const handleDeleteTargetedKeywords = async (campaign) => {
    if (!window.confirm(`Delete all targeted keywords for "${campaign}"?`)) return;
    
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}`, campaign };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      
      await axios.delete(`${API}/integrations/google-ads/targeted-keywords`, { params });
      toast.success(`Deleted targeted keywords for "${campaign}"`);
      loadExistingTargetedKeywords();
    } catch (error) {
      toast.error("Failed to delete: " + (error.response?.data?.detail || error.message));
    }
  };

  // Trigger HubSpot sync manually
  const triggerHubSpotSync = async () => {
    setSyncingHubSpot(true);
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      await axios.post(`${API}/trigger-sync`, {}, { params });
      toast.success("HubSpot sync started! This may take 10-30 minutes for large datasets. Check back shortly.");
      
      // Poll for sync status updates
      setTimeout(() => fetchSyncStatus(), 5000);
    } catch (error) {
      console.error("Error triggering sync:", error);
      toast.error("Failed to trigger sync: " + (error.response?.data?.detail || error.message));
    } finally {
      setSyncingHubSpot(false);
    }
  };

  // Trigger LinkedIn Follower History Sync
  const syncLinkedInFollowerHistory = async () => {
    setSyncingLinkedInFollowers(true);
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const response = await axios.post(`${API}/integrations/linkedin/sync-follower-history`, {}, {
        params: { 
          Authorization: `Bearer ${sessionToken}`,
          "X-Tenant-ID": viewingTenantId
        }
      });
      toast.success(`LinkedIn follower history synced! ${response.data.records_saved} daily records saved (${response.data.date_range?.start} to ${response.data.date_range?.end})`);
    } catch (error) {
      console.error("Error syncing LinkedIn follower history:", error);
      toast.error("Failed to sync LinkedIn follower history: " + (error.response?.data?.detail || error.message));
    } finally {
      setSyncingLinkedInFollowers(false);
    }
  };

  // Fetch sync status
  const fetchSyncStatus = async () => {
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      const response = await axios.get(`${API}/sync-status`, { params });
      setSyncStatus(response.data);
    } catch (error) {
      console.error("Error fetching sync status:", error);
    }
  };

  const connectHubSpot = async () => {
    setConnectingHubSpot(true);
    try {
      // CRITICAL: Pass X-Tenant-ID so the OAuth state includes the correct tenant
      // This ensures the HubSpot token is stored for this tenant, not the super-admin
      const currentViewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (currentViewingTenantId) {
        params["X-Tenant-ID"] = currentViewingTenantId;
      }
      const response = await axios.get(`${API}/integrations/hubspot/auth`, { params });
      // Open HubSpot OAuth in new window
      window.open(response.data.auth_url, "_blank", "width=600,height=700");
      toast.info("Complete the HubSpot authorization in the popup window");
    } catch (error) {
      console.error("Error connecting HubSpot:", error);
      toast.error("Failed to initiate HubSpot connection");
    } finally {
      setConnectingHubSpot(false);
    }
  };

  const connectLinkedIn = async () => {
    setConnectingLinkedIn(true);
    try {
      // CRITICAL: Pass X-Tenant-ID so the OAuth state includes the correct tenant
      const currentViewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (currentViewingTenantId) {
        params["X-Tenant-ID"] = currentViewingTenantId;
      }
      const response = await axios.get(`${API}/integrations/linkedin/auth`, { params });
      window.open(response.data.auth_url, "_blank", "width=600,height=700");
      toast.info("Complete the LinkedIn authorization in the popup window");
    } catch (error) {
      console.error("Error connecting LinkedIn:", error);
      toast.error("Failed to initiate LinkedIn connection");
    } finally {
      setConnectingLinkedIn(false);
    }
  };

  const connectGoogleAds = async () => {
    setConnectingGoogleAds(true);
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      const response = await axios.get(`${API}/integrations/google-ads/auth`, { params });
      window.open(response.data.auth_url, "_blank", "width=600,height=700");
      toast.info("Complete the Google Ads authorization in the popup window");
    } catch (error) {
      console.error("Error connecting Google Ads:", error);
      toast.error("Failed to initiate Google Ads connection");
    } finally {
      setConnectingGoogleAds(false);
    }
  };

  const connectGA4 = async () => {
    setConnectingGA4(true);
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      const response = await axios.get(`${API}/integrations/ga4/auth`, { params });
      window.open(response.data.auth_url, "_blank", "width=600,height=700");
      toast.info("Complete the Google Analytics authorization in the popup window");
    } catch (error) {
      console.error("Error connecting GA4:", error);
      toast.error("Failed to initiate GA4 connection");
    } finally {
      setConnectingGA4(false);
    }
  };

  const connectGSC = async () => {
    setConnectingGSC(true);
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (!viewingTenantId) {
        toast.error("No tenant selected");
        return;
      }
      
      const response = await axios.get(`${API}/auth/gsc/connect`, {
        params: {
          tenant_id: viewingTenantId,
          origin: window.location.origin
        }
      });
      
      // Redirect to Google OAuth (full page redirect for account selection)
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error("Error connecting GSC:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate GSC connection");
    } finally {
      setConnectingGSC(false);
    }
  };

  const handleWebsiteVisitsUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingVisits(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }

      const response = await axios.post(
        `${API}/integrations/website-visits/upload`,
        formData,
        {
          params,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast.success(`Imported ${response.data.imported} website visits`);
      
      // Fetch updated stats
      const statsResponse = await axios.get(`${API}/integrations/website-visits`, {
        params: { Authorization: `Bearer ${sessionToken}`, ...( viewingTenantId && { "X-Tenant-ID": viewingTenantId }) },
      });
      setWebsiteVisitsStats(statsResponse.data.stats);
    } catch (error) {
      console.error("Error uploading website visits:", error);
      toast.error(error.response?.data?.detail || "Failed to import website visits");
    } finally {
      setUploadingVisits(false);
      event.target.value = ""; // Reset file input
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (PNG, JPG)");
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be less than 2MB");
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }

      const response = await axios.post(`${API}/admin/upload-logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        params,
      });

      setLogoPreview(response.data.logo_url);
      setSettings({
        ...settings,
        company_logo: response.data.logo_url,
      });
      toast.success("Logo uploaded successfully");
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      event.target.value = "";
    }
  };

  const fetchWebsiteVisitsStats = async () => {
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}`, limit: 1 };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      const response = await axios.get(`${API}/integrations/website-visits`, { params });
      if (response.data.stats.total_companies > 0) {
        setWebsiteVisitsStats(response.data.stats);
      }
    } catch (error) {
      console.error("Error fetching website visits stats:", error);
    }
  };

  const disconnectIntegration = async (integration) => {
    console.log(`Disconnect clicked for: ${integration}`);
    
    if (!window.confirm(`Are you sure you want to disconnect ${integration}? You will need to re-authorize to reconnect.`)) {
      console.log("User cancelled disconnect");
      return;
    }
    
    console.log(`Proceeding with disconnect for: ${integration}`);
    
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      console.log("Calling disconnect API...");
      await axios.delete(`${API}/integrations/${integration}/disconnect`, { params });
      console.log("Disconnect successful");
      toast.success(`${integration} disconnected successfully`);
      fetchIntegrationStatus();
      if (integration === "linkedin") {
        setLinkedInOrgs([]);
        setSelectedLinkedInOrg(null);
      }
    } catch (error) {
      console.error(`Error disconnecting ${integration}:`, error);
      toast.error(`Failed to disconnect ${integration}`);
    }
  };

  const fetchLinkedInOrganizations = async () => {
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      const response = await axios.get(`${API}/integrations/linkedin/organizations`, { params });
      setLinkedInOrgs(response.data.organizations || []);
      setSelectedLinkedInOrg(response.data.selected_org_id);
    } catch (error) {
      console.error("Error fetching LinkedIn organizations:", error);
    }
  };

  const selectLinkedInOrganization = async (orgId) => {
    setSelectingOrg(true);
    try {
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      const response = await axios.post(
        `${API}/integrations/linkedin/select-organization`,
        { organization_id: orgId },
        { params }
      );
      toast.success(`Connected to ${response.data.analytics.followers.toLocaleString()} followers`);
      setSelectedLinkedInOrg(orgId);
      fetchIntegrationStatus();
    } catch (error) {
      console.error("Error selecting LinkedIn organization:", error);
      toast.error(error.response?.data?.detail || "Failed to select organization");
    } finally {
      setSelectingOrg(false);
    }
  };

  useEffect(() => {
    fetchWebsiteVisitsStats();
    
    // Check URL params for OAuth callback results
    const params = new URLSearchParams(window.location.search);
    if (params.get("linkedin") === "connected") {
      toast.success("LinkedIn connected successfully! Select a company page below.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("linkedin_ads") === "connected") {
      toast.success("LinkedIn Ads/Events connected successfully!");
      window.history.replaceState({}, "", window.location.pathname);
      fetchIntegrationStatus();
    }
    if (params.get("hubspot") === "connected") {
      toast.success("HubSpot connected successfully!");
      window.history.replaceState({}, "", window.location.pathname);
      fetchIntegrationStatus();
    }
    if (params.get("google_ads") === "connected") {
      toast.success("Google Ads connected successfully!");
      window.history.replaceState({}, "", window.location.pathname);
      fetchIntegrationStatus();
    }
    if (params.get("ga4") === "connected") {
      toast.success("Google Analytics connected successfully!");
      window.history.replaceState({}, "", window.location.pathname);
      fetchIntegrationStatus();
    }
    if (params.get("error")) {
      toast.error(`Connection failed: ${params.get("error").replace(/_/g, " ")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [sessionToken]);

  // Fetch LinkedIn orgs when status shows connected
  useEffect(() => {
    if (integrationStatus?.linkedin?.connected) {
      fetchLinkedInOrganizations();
    }
  }, [integrationStatus?.linkedin?.connected]);

  const updateUserRole = async (userId, newRole) => {
    if (!isMasterAdmin) {
      toast.error("Only master admins can change roles");
      return;
    }

    try {
      await axios.put(
        `${API}/users/${userId}/role`,
        { role: newRole },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("User role updated");
      fetchUsers();
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Failed to update role");
    }
  };

  // Add new user
  const addUser = async () => {
    if (!newUserData.name || !newUserData.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setAddingUser(true);
    try {
      await axios.post(
        `${API}/users`,
        newUserData,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("User added successfully");
      setShowAddUserDialog(false);
      setNewUserData({ name: "", email: "", role: "user", password: "" });
      fetchUsers();
    } catch (error) {
      console.error("Error adding user:", error);
      toast.error(error.response?.data?.detail || "Failed to add user");
    } finally {
      setAddingUser(false);
    }
  };

  // Suspend user
  const suspendUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to suspend ${userName}? They will be logged out immediately.`)) {
      return;
    }
    
    try {
      await axios.put(
        `${API}/users/${userId}/suspend`,
        {},
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("User suspended");
      fetchUsers();
    } catch (error) {
      console.error("Error suspending user:", error);
      toast.error(error.response?.data?.detail || "Failed to suspend user");
    }
  };

  // Activate user
  const activateUser = async (userId) => {
    try {
      await axios.put(
        `${API}/users/${userId}/activate`,
        {},
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("User activated");
      fetchUsers();
    } catch (error) {
      console.error("Error activating user:", error);
      toast.error(error.response?.data?.detail || "Failed to activate user");
    }
  };

  // Delete user
  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${userName}? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(
        `${API}/users/${userId}`,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  // Connect LinkedIn Ads/Events
  const connectLinkedInAds = async () => {
    setConnectingLinkedInAds(true);
    try {
      // CRITICAL: Pass X-Tenant-ID so the OAuth state includes the correct tenant
      const currentViewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (currentViewingTenantId) {
        params["X-Tenant-ID"] = currentViewingTenantId;
      }
      const response = await axios.get(`${API}/integrations/linkedin-ads/auth`, { params });
      window.open(response.data.auth_url, "_blank", "width=600,height=700");
      toast.info("Complete the LinkedIn authorization in the popup window");
    } catch (error) {
      console.error("Error connecting LinkedIn Ads:", error);
      toast.error("Failed to initiate LinkedIn Ads connection");
    } finally {
      setConnectingLinkedInAds(false);
    }
  };

  // Fetch dev notes
  const fetchDevNotes = async () => {
    if (!canSeeNotes) return;
    setLoadingNotes(true);
    try {
      const response = await axios.get(`${API}/admin/notes`, {
        params: { Authorization: `Bearer ${sessionToken}` },
      });
      setDevNotes(response.data.content || "");
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setLoadingNotes(false);
    }
  };

  // Save dev notes
  const saveDevNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.put(
        `${API}/admin/notes`,
        { content: devNotes },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("Notes saved");
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  // Fetch webhook settings
  const fetchWebhookSettings = async () => {
    if (!canSeeNotes) return;
    setLoadingWebhook(true);
    try {
      const response = await axios.get(`${API}/admin/competitor-settings`, {
        params: { Authorization: `Bearer ${sessionToken}` },
      });
      const data = response.data;
      setWebhookSettings({
        webhook_url: data.webhook_url || "",
        competitors: data.competitors?.length > 0 
          ? [...data.competitors, "", "", ""].slice(0, 3) 
          : ["", "", ""]
      });
    } catch (error) {
      console.error("Error fetching webhook settings:", error);
    } finally {
      setLoadingWebhook(false);
    }
  };

  // Save webhook settings
  const saveWebhookSettings = async () => {
    setSavingWebhook(true);
    try {
      const competitors = webhookSettings.competitors.filter(c => c.trim() !== "");
      await axios.put(
        `${API}/admin/competitor-settings`,
        { 
          webhook_url: webhookSettings.webhook_url,
          competitors: competitors
        },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("Webhook settings saved");
    } catch (error) {
      console.error("Error saving webhook settings:", error);
      toast.error("Failed to save webhook settings");
    } finally {
      setSavingWebhook(false);
    }
  };

  // Fetch webhook secret
  const fetchWebhookSecret = async () => {
    if (!canSeeNotes) return;
    setLoadingSecret(true);
    try {
      const response = await axios.get(`${API}/admin/webhook-secret`, {
        params: { Authorization: `Bearer ${sessionToken}` },
      });
      setWebhookSecret(response.data.webhook_secret);
    } catch (error) {
      console.error("Error fetching webhook secret:", error);
    } finally {
      setLoadingSecret(false);
    }
  };

  // Copy webhook secret to clipboard
  const copySecretToClipboard = () => {
    if (webhookSecret) {
      navigator.clipboard.writeText(webhookSecret);
      setCopiedSecret(true);
      toast.success("Webhook secret copied to clipboard!");
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  // Fetch notes on mount if user can see them
  useEffect(() => {
    if (canSeeNotes) {
      fetchDevNotes();
      fetchWebhookSettings();
      fetchWebhookSecret();
    }
  }, [canSeeNotes]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Get current tenant context
      const tenantId = sessionStorage.getItem("viewing_tenant_id");
      
      // Ensure chart colors always have primary/secondary as first two colors
      const primary = settings?.default_brand_colors?.primary || "#00A5A8";
      const secondary = settings?.default_brand_colors?.secondary || "#0F172A";
      const currentChartColors = settings?.default_brand_colors?.chart_colors || ["#00A5A8", "#0F172A", "#0EA5E9", "#64748B", "#10B981"];
      const syncedChartColors = [primary, secondary, ...currentChartColors.slice(2)];
      
      if (tenantId) {
        // Save to tenant-specific branding endpoint
        const brandingData = {
          logo_url: settings?.company_logo || null,
          primary_color: primary,
          secondary_color: secondary,
          chart_colors: syncedChartColors
        };
        
        console.log("Saving branding for tenant:", tenantId, brandingData);
        
        await axios.put(`${API}/tenants/${tenantId}/branding`, brandingData, {
          params: { Authorization: `Bearer ${sessionToken}` },
        });
        
        // Also save stage mapping if it exists - parse raw text into arrays
        const stageMapping = {
          mql_stages: mqlStagesText.split(/[,\n]+/).map(s => s.trim()).filter(Boolean),
          sql_stages: sqlStagesText.split(/[,\n]+/).map(s => s.trim()).filter(Boolean),
          closed_won_stages: closedWonStagesText.split(/[,\n]+/).map(s => s.trim()).filter(Boolean)
        };
        
        if (stageMapping.mql_stages.length > 0 || stageMapping.sql_stages.length > 0 || stageMapping.closed_won_stages.length > 0) {
          console.log("Saving stage mapping:", stageMapping);
          await axios.put(`${API}/tenants/${tenantId}/stage-mapping`, 
            stageMapping,
            { params: { Authorization: `Bearer ${sessionToken}` } }
          );
        }
        
        // Save Deals ICP criteria if present
        if (dealsIcpCriteria) {
          await axios.put(`${API}/tenants/${tenantId}`, 
            { deals_icp_criteria: dealsIcpCriteria },
            { params: { Authorization: `Bearer ${sessionToken}` } }
          );
        }
        
        // Save Social Insights visibility settings if present (super admin only)
        // Also save platform_name and company_name
        const settingsData = {};
        
        if (settings?.social_insights) {
          settingsData.show_page_views_chart = settings.social_insights.show_page_views_chart ?? true;
          settingsData.show_engagement_chart = settings.social_insights.show_engagement_chart ?? true;
          settingsData.show_page_views_table = settings.social_insights.show_page_views_table ?? true;
          settingsData.show_additional_metrics = settings.social_insights.show_additional_metrics ?? true;
        }
        
        // Always save platform_name and company_name if they exist
        if (settings?.platform_name !== undefined) {
          settingsData.platform_name = settings.platform_name;
        }
        if (settings?.company_name !== undefined) {
          settingsData.company_name = settings.company_name;
        }
        
        if (Object.keys(settingsData).length > 0) {
          console.log("Saving tenant settings:", settingsData);
          await axios.put(`${API}/tenants/${tenantId}/settings`, 
            settingsData,
            { params: { Authorization: `Bearer ${sessionToken}` } }
          );
        }
      } else {
        // Fallback to global admin settings (for users without tenant context)
        const settingsToSave = {
          ...settings,
          default_brand_colors: {
            ...settings?.default_brand_colors,
            chart_colors: syncedChartColors
          }
        };
        
        await axios.put(`${API}/admin/settings`, settingsToSave, {
          params: { Authorization: `Bearer ${sessionToken}` },
        });
        
        setSettings(settingsToSave);
      }
      
      toast.success("Settings saved!");
      // Refresh branding context to update chart colors across the app
      refreshBranding();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadge = (role) => {
    const variants = {
      master_admin: "bg-red-100 text-red-700",
      admin: "bg-purple-100 text-purple-700",
      user: "bg-slate-100 text-slate-700",
    };
    return (
      <Badge className={variants[role] || variants.user}>
        {role?.replace("_", " ") || "user"}
      </Badge>
    );
  };

  const getContrastRatio = (color1, color2) => {
    const getLuminance = (hex) => {
      const rgb = parseInt(hex.slice(1), 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = (rgb >> 0) & 0xff;
      const a = [r, g, b].map((v) => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      });
      return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
    };
    const l1 = getLuminance(color1);
    const l2 = getLuminance(color2);
    const ratio = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
    return ratio.toFixed(2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="admin-panel-page">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Settings className="w-6 h-6 text-[#00A5A8]" />
            Settings
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage users and instance configuration
          </p>
        </div>
      </div>

      <Tabs defaultValue="integrations" className="space-y-6">
        <TabsList className="bg-slate-100 flex-wrap">
          <TabsTrigger value="integrations" className="gap-2">
            <Link2 className="w-4 h-4" />
            Integrations
          </TabsTrigger>
          {/* Branding tab - only for super admins or branding admins */}
          {(isSuperAdmin || user?.role === 'branding_admin') && (
            <TabsTrigger value="branding" className="gap-2">
              <Palette className="w-4 h-4" />
              Branding
            </TabsTrigger>
          )}
          <TabsTrigger value="users" className="gap-2">
            <Users className="w-4 h-4" />
            Users
          </TabsTrigger>
          {/* Features tab - super admin only (this is the ONLY hidden tab) */}
          {isSuperAdmin && (
            <TabsTrigger value="features" className="gap-2">
              <Settings className="w-4 h-4" />
              Features
            </TabsTrigger>
          )}
          <TabsTrigger value="stages" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Stage Mapping
          </TabsTrigger>
          <TabsTrigger value="goals" className="gap-2">
            <Target className="w-4 h-4" />
            Goals
          </TabsTrigger>
          <TabsTrigger value="icp" className="gap-2">
            <Target className="w-4 h-4" />
            ICP
          </TabsTrigger>
          {canSeeNotes && (
            <TabsTrigger value="notes" className="gap-2">
              <FileText className="w-4 h-4" />
              Notes
            </TabsTrigger>
          )}
          {/* Backup tab - super admin only */}
          {isSuperAdmin && (
            <TabsTrigger value="database" className="gap-2">
              <Save className="w-4 h-4" />
              Backup
            </TabsTrigger>
          )}
        </TabsList>

        {/* Integrations Tab */}
        <TabsContent value="integrations">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* HubSpot */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#FF7A59">
                        <path d="M18.16 5.67c-.58-.29-1.24-.45-1.92-.45-2.35 0-4.27 1.92-4.27 4.27 0 .68.16 1.34.45 1.92l-3.07 3.07c-.58-.29-1.24-.45-1.92-.45-2.35 0-4.27 1.92-4.27 4.27s1.92 4.27 4.27 4.27 4.27-1.92 4.27-4.27c0-.68-.16-1.34-.45-1.92l3.07-3.07c.58.29 1.24.45 1.92.45 2.35 0 4.27-1.92 4.27-4.27s-1.92-4.27-4.27-4.27z"/>
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-lg">HubSpot</CardTitle>
                      <CardDescription>CRM, contacts, deals, form submissions</CardDescription>
                    </div>
                  </div>
                  {integrationStatus?.hubspot?.connected ? (
                    <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Connected
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600 gap-1">
                      <XCircle className="w-3 h-3" />
                      Not Connected
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {integrationStatus?.hubspot?.connected ? (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600">
                      Connected on {integrationStatus.hubspot.connected_at 
                        ? new Date(integrationStatus.hubspot.connected_at).toLocaleDateString() 
                        : "Unknown date"}
                    </p>
                    
                    {/* Sync Status Display - Super Admin Only */}
                    {user?.role === 'super_admin' && syncStatus && (
                      <div className="p-3 bg-slate-50 rounded-lg text-xs space-y-1">
                        <p className="font-medium text-slate-700">Sync Status:</p>
                        {syncStatus.tenants?.map(t => (
                          <div key={t.tenant_id} className="flex justify-between">
                            <span className="text-slate-600">{t.name}:</span>
                            <span className="font-mono">{t.contacts_in_db?.toLocaleString() || 0} contacts</span>
                          </div>
                        ))}
                        {syncStatus.next_scheduled_sync && (
                          <p className="text-slate-500 mt-2">
                            Next auto-sync: {new Date(syncStatus.next_scheduled_sync).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                    
                    <div className="flex flex-wrap gap-2">
                      {canSeeRefreshButtons && (
                        <Button variant="outline" size="sm" onClick={() => fetchIntegrationStatus()}>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Refresh
                        </Button>
                      )}
                      
                      {/* Sync Now Button - Super Admin Only */}
                      {user?.role === 'super_admin' && (
                        <Button 
                          variant="default" 
                          size="sm" 
                          onClick={triggerHubSpotSync}
                          disabled={syncingHubSpot}
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                        >
                          {syncingHubSpot ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-2" />
                          )}
                          Sync Now
                        </Button>
                      )}
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => disconnectIntegration("hubspot")}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Disconnect
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    onClick={connectHubSpot} 
                    disabled={connectingHubSpot}
                    className="w-full bg-[#FF7A59] hover:bg-[#ff6a47] text-white"
                  >
                    {connectingHubSpot ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ExternalLink className="w-4 h-4 mr-2" />
                    )}
                    Connect HubSpot
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* LinkedIn */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#0A66C2">
                        <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/>
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-lg">LinkedIn</CardTitle>
                      <CardDescription>Company pages, social engagement</CardDescription>
                    </div>
                  </div>
                  {integrationStatus?.linkedin?.connected && integrationStatus?.linkedin?.selected_org_id ? (
                    <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Connected
                    </Badge>
                  ) : integrationStatus?.linkedin?.connected ? (
                    <Badge className="bg-amber-100 text-amber-700 gap-1">
                      <Building className="w-3 h-3" />
                      Select Page
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600 gap-1">
                      <XCircle className="w-3 h-3" />
                      Not Connected
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {integrationStatus?.linkedin?.connected ? (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600">
                      Connected on {integrationStatus.linkedin.connected_at 
                        ? new Date(integrationStatus.linkedin.connected_at).toLocaleDateString()
                        : "Unknown date"}
                    </p>
                    {integrationStatus.linkedin.organization?.id && (
                      <p className="text-sm text-slate-700 bg-slate-50 p-2 rounded">
                        <strong>{integrationStatus.linkedin.organization.name || "LinkedIn Page"}</strong>
                        <span className="text-slate-500 ml-2">(ID: {integrationStatus.linkedin.organization.id})</span>
                      </p>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      {canSeeRefreshButtons && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={syncLinkedInFollowerHistory}
                          disabled={syncingLinkedInFollowers}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          {syncingLinkedInFollowers ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-2" />
                          )}
                          Sync Follower History
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => disconnectIntegration("linkedin")}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Disconnect
                      </Button>
                    </div>
                  </div>
                ) : connectionPermissions?.linkedin?.can_connect === false ? (
                  <div className="space-y-3">
                    <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                      LinkedIn is already connected by <strong>{connectionPermissions.linkedin.connected_by}</strong>. 
                      Only the user who connected or a super admin can manage this integration.
                    </p>
                    <Button 
                      disabled
                      className="w-full bg-slate-300 text-slate-500 cursor-not-allowed"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Connect LinkedIn Pages
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={connectLinkedIn} 
                    disabled={connectingLinkedIn}
                    className="w-full bg-[#0A66C2] hover:bg-[#004182] text-white"
                  >
                    {connectingLinkedIn ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ExternalLink className="w-4 h-4 mr-2" />
                    )}
                    Connect LinkedIn Pages
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* LinkedIn Ads/Events - Connect for Paid Impressions */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">LinkedIn Ads</CardTitle>
                      <CardDescription>Paid impressions, ad performance</CardDescription>
                    </div>
                  </div>
                  {integrationStatus?.linkedin_ads?.connected ? (
                    <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Connected
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 gap-1">
                      ⏳ Processing
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {integrationStatus?.linkedin_ads?.connected ? (
                  <p className="text-sm text-slate-600">
                    LinkedIn Ads connected. Paid impressions syncing.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-amber-700">
                      Note: LinkedIn Advertising API takes ~1 week to activate after app approval.
                    </p>
                    <Button 
                      onClick={connectLinkedInAds}
                      disabled={connectingLinkedInAds}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {connectingLinkedInAds ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <Linkedin className="w-4 h-4 mr-2" />
                          Connect LinkedIn Ads
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Google Ads - Integration button only for super admins */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                      <svg className="w-6 h-6" viewBox="0 0 24 24">
                        <path fill="#FBBC04" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
                        <path fill="#EA4335" d="M12 2v10l8.66 5A10 10 0 0012 2z"/>
                        <path fill="#34A853" d="M12 12v10a10 10 0 008.66-5L12 12z"/>
                        <path fill="#4285F4" d="M12 12L3.34 17A10 10 0 0012 22V12z"/>
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-lg">Google Ads</CardTitle>
                      <CardDescription>Campaign keywords, ad performance</CardDescription>
                    </div>
                  </div>
                  {integrationStatus?.google_ads?.connected && (
                    <Badge className="bg-green-100 text-green-700 gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Connected
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {integrationStatus?.google_ads?.connected ? (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600">
                      Connected on {integrationStatus.google_ads.connected_at 
                        ? new Date(integrationStatus.google_ads.connected_at).toLocaleDateString()
                        : "Unknown date"}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => disconnectIntegration("google_ads")}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Disconnect
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-slate-500">
                      Google Ads data is managed via CSV upload.
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      API integration coming soon.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* GA4 */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                      <svg className="w-6 h-6" viewBox="0 0 24 24">
                        <path fill="#E37400" d="M22 12c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2s10 4.48 10 10z"/>
                        <path fill="#FFC107" d="M12 6v6l4 2"/>
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-lg">Google Analytics 4</CardTitle>
                      <CardDescription>Website traffic, sessions, engagement</CardDescription>
                    </div>
                  </div>
                  {integrationStatus?.ga4?.connected ? (
                    <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Connected
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600 gap-1">
                      <XCircle className="w-3 h-3" />
                      Not Connected
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {integrationStatus?.ga4?.connected ? (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600">
                      Connected on {integrationStatus.ga4.connected_at 
                        ? new Date(integrationStatus.ga4.connected_at).toLocaleDateString()
                        : "Unknown date"}
                    </p>
                    <div className="flex gap-2">
                      {canSeeRefreshButtons && (
                        <Button variant="outline" size="sm" onClick={() => fetchIntegrationStatus()}>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Refresh
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => disconnectIntegration("ga4")}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Disconnect
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    onClick={connectGA4} 
                    disabled={connectingGA4}
                    className="w-full bg-[#E37400] hover:bg-[#c96700] text-white"
                  >
                    {connectingGA4 ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ExternalLink className="w-4 h-4 mr-2" />
                    )}
                    Connect Google Analytics
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Google Search Console */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                      <Search className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Google Search Console</CardTitle>
                      <CardDescription>Organic search queries, clicks, impressions</CardDescription>
                    </div>
                  </div>
                  {integrationStatus?.gsc?.connected ? (
                    <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Connected
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600 gap-1">
                      <XCircle className="w-3 h-3" />
                      Not Connected
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {integrationStatus?.gsc?.connected ? (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600">
                      Site: <span className="font-medium">{integrationStatus.gsc.site_url}</span>
                    </p>
                    <p className="text-sm text-slate-500">
                      Connected on {integrationStatus.gsc.connected_at 
                        ? new Date(integrationStatus.gsc.connected_at).toLocaleDateString()
                        : "Unknown date"}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => window.location.href = "/app/goals/gsc-tracking"}
                      >
                        <Search className="w-4 h-4 mr-2" />
                        View Rankings
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => disconnectIntegration("gsc")}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Disconnect
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    onClick={connectGSC} 
                    disabled={connectingGSC}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="connect-gsc-admin-btn"
                  >
                    {connectingGSC ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ExternalLink className="w-4 h-4 mr-2" />
                    )}
                    Connect Google Search Console
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Website Visits Upload */}
          <Card className="chart-container mt-6">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Upload className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Website Visits Import</CardTitle>
                  <CardDescription>
                    Upload HubSpot Website visits by company export (Excel file)
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleWebsiteVisitsUpload}
                    className="hidden"
                    id="website-visits-upload"
                  />
                  <label 
                    htmlFor="website-visits-upload" 
                    className="cursor-pointer"
                  >
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600 font-medium">
                      Click to upload HubSpot export
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Export from HubSpot: Reports → Website Visits → Export All
                    </p>
                  </label>
                </div>
                {uploadingVisits && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Importing website visits...
                  </div>
                )}
                {websiteVisitsStats && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-sm text-emerald-800">
                      <CheckCircle2 className="w-4 h-4 inline mr-1" />
                      {websiteVisitsStats.total_companies} companies loaded • {websiteVisitsStats.total_page_views.toLocaleString()} page views
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Google Ads CSV Upload - Show if enabled for tenant, or if super admin/impersonating */}
          {(featureSettings.show_google_ads_upload || isSuperAdmin) && (
            <Card className="chart-container mt-6 border-yellow-200 bg-yellow-50/30">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <svg className="w-6 h-6" viewBox="0 0 24 24">
                      <path fill="#FBBC04" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
                      <path fill="#EA4335" d="M12 2v10l8.66 5A10 10 0 0012 2z"/>
                      <path fill="#34A853" d="M12 12v10a10 10 0 008.66-5L12 12z"/>
                      <path fill="#4285F4" d="M12 12L3.34 17A10 10 0 0012 22V12z"/>
                    </svg>
                  </div>
                  <div>
                    <CardTitle className="text-lg">Google Ads CSV Import</CardTitle>
                    <CardDescription>
                      Upload campaign and keyword data exported from Google Ads
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="ml-auto text-yellow-700 border-yellow-400 bg-yellow-100">
                    Manual Import
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-white border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800 mb-2">
                      <strong>How to export from Google Ads:</strong>
                    </p>
                    <ol className="text-xs text-yellow-700 space-y-1 list-decimal list-inside">
                      <li>Go to Reports → Predefined reports → Basic → Campaign</li>
                      <li>Set date range (e.g., last 365 days) and include Day, Campaign, Impressions, Clicks, Cost, Conversions</li>
                      <li>Click Download → CSV (.csv)</li>
                      <li>For keywords: Go to Keywords → Search keywords → Download CSV</li>
                    </ol>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Campaign CSV Upload */}
                    <div className="border-2 border-dashed border-yellow-200 rounded-lg p-4 text-center bg-white">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleGoogleAdsCampaignUpload}
                        className="hidden"
                        id="google-ads-campaign-upload"
                        disabled={uploadingGoogleAdsCampaigns}
                      />
                      <label htmlFor="google-ads-campaign-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                        <p className="text-sm text-slate-600 font-medium">Campaign Report CSV</p>
                        <p className="text-xs text-slate-400 mt-1">Daily campaign performance data</p>
                      </label>
                    </div>

                    {/* Keyword CSV Upload */}
                    <div className="border-2 border-dashed border-yellow-200 rounded-lg p-4 text-center bg-white">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleGoogleAdsKeywordUpload}
                        className="hidden"
                        id="google-ads-keyword-upload"
                        disabled={uploadingGoogleAdsKeywords}
                      />
                      <label htmlFor="google-ads-keyword-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                        <p className="text-sm text-slate-600 font-medium">Keyword Report CSV</p>
                        <p className="text-xs text-slate-400 mt-1">Search keyword performance data</p>
                      </label>
                    </div>
                  </div>

                  {(uploadingGoogleAdsCampaigns || uploadingGoogleAdsKeywords) && (
                    <div className="flex items-center gap-2 text-sm text-yellow-700">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Uploading Google Ads data...
                    </div>
                  )}

                  {googleAdsUploadStats && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800">
                        <CheckCircle2 className="w-4 h-4 inline mr-1" />
                        {googleAdsUploadStats.campaigns?.records && `${googleAdsUploadStats.campaigns.records} campaign records`}
                        {googleAdsUploadStats.campaigns?.records && googleAdsUploadStats.keywords?.records && ' • '}
                        {googleAdsUploadStats.keywords?.records && `${googleAdsUploadStats.keywords.records} keyword records`}
                      </p>
                    </div>
                  )}

                  {/* Google Ads Data Status & Upload History */}
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
                    <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      Current Data Status
                    </h4>
                    
                    {googleAdsUploadHistory?.current_data ? (
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="p-2 bg-white rounded border">
                          <span className="text-slate-500">Campaigns:</span>
                          <span className="ml-2 font-semibold text-slate-800">
                            {googleAdsUploadHistory.current_data.campaigns?.toLocaleString() || 0}
                          </span>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <span className="text-slate-500">Keywords:</span>
                          <span className="ml-2 font-semibold text-slate-800">
                            {googleAdsUploadHistory.current_data.keywords?.toLocaleString() || 0}
                          </span>
                        </div>
                        {googleAdsUploadHistory.current_data.date_range?.start && (
                          <div className="col-span-2 p-2 bg-white rounded border">
                            <span className="text-slate-500">Date Range:</span>
                            <span className="ml-2 font-semibold text-slate-800">
                              {googleAdsUploadHistory.current_data.date_range.start} to {googleAdsUploadHistory.current_data.date_range.end}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 italic">No Google Ads data uploaded yet</p>
                    )}
                    
                    {/* Upload History */}
                    {googleAdsUploadHistory?.uploads?.length > 0 && (
                      <div className="pt-3 border-t border-slate-200">
                        <h5 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Recent Uploads</h5>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {googleAdsUploadHistory.uploads.slice(0, 5).map((upload, idx) => (
                            <div key={idx} className="p-2 bg-white rounded border text-xs flex justify-between items-center">
                              <div>
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium mr-2 ${
                                  upload.upload_type === 'campaigns' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {upload.upload_type}
                                </span>
                                <span className="text-slate-600">{upload.records_count?.toLocaleString()} records</span>
                                {upload.date_range && (
                                  <span className="text-slate-400 ml-2">
                                    ({upload.date_range.start} - {upload.date_range.end})
                                  </span>
                                )}
                              </div>
                              <div className="text-slate-400">
                                {new Date(upload.uploaded_at).toLocaleDateString()}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Targeted Keywords Upload Section */}
                  <div className="pt-4 border-t border-yellow-200">
                    <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4 text-yellow-600" />
                      Targeted Keywords by Campaign
                    </h4>
                    <p className="text-xs text-slate-500 mb-3">
                      Upload keywords you're targeting for specific campaigns. These will appear in a dedicated tab on the Google Ads page.
                    </p>

                    {/* Campaign Selection */}
                    <div className="space-y-3 mb-4">
                      <div>
                        <label className="text-xs font-medium text-slate-700 block mb-1">Campaign</label>
                        <div className="flex gap-2">
                          <select
                            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400"
                            value={targetedKeywordsCampaign}
                            onChange={(e) => {
                              setTargetedKeywordsCampaign(e.target.value);
                              setTargetedKeywordsCustomCampaign('');
                            }}
                          >
                            <option value="">Select from your campaigns...</option>
                            {activeCampaigns.map((camp, idx) => (
                              <option key={idx} value={camp.campaign}>
                                {camp.campaign} ({camp.impressions?.toLocaleString()} imp)
                              </option>
                            ))}
                            <option value="__custom__">Enter custom campaign name...</option>
                          </select>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={loadActiveCampaigns}
                            disabled={loadingCampaigns}
                          >
                            <RefreshCw className={`w-4 h-4 ${loadingCampaigns ? 'animate-spin' : ''}`} />
                          </Button>
                        </div>
                      </div>

                      {targetedKeywordsCampaign === '__custom__' && (
                        <div>
                          <label className="text-xs font-medium text-slate-700 block mb-1">Custom Campaign Name</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                            placeholder="Enter campaign name exactly as it appears in Google Ads"
                            value={targetedKeywordsCustomCampaign}
                            onChange={(e) => setTargetedKeywordsCustomCampaign(e.target.value)}
                          />
                        </div>
                      )}
                    </div>

                    {/* Keywords Textarea */}
                    <div className="mb-4">
                      <label className="text-xs font-medium text-slate-700 block mb-1">
                        Keywords (one per line, or comma-separated)
                      </label>
                      <textarea
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 font-mono"
                        rows={6}
                        placeholder="keyword 1&#10;keyword 2&#10;keyword 3&#10;&#10;OR&#10;&#10;keyword 1, keyword 2, keyword 3"
                        value={targetedKeywordsText}
                        onChange={(e) => setTargetedKeywordsText(e.target.value)}
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        {targetedKeywordsText.trim() 
                          ? `${targetedKeywordsText.includes('\n') 
                              ? targetedKeywordsText.split('\n').filter(k => k.trim()).length 
                              : targetedKeywordsText.split(',').filter(k => k.trim()).length
                            } keywords detected`
                          : 'Paste up to 200 keywords'}
                      </p>
                    </div>

                    {/* Upload Button */}
                    <Button
                      className="w-full bg-yellow-500 hover:bg-yellow-600 text-white"
                      onClick={handleTargetedKeywordsUpload}
                      disabled={uploadingTargetedKeywords || !targetedKeywordsText.trim() || (!targetedKeywordsCampaign || (targetedKeywordsCampaign === '__custom__' && !targetedKeywordsCustomCampaign.trim()))}
                    >
                      {uploadingTargetedKeywords ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Targeted Keywords
                        </>
                      )}
                    </Button>

                    {/* Existing Targeted Keywords */}
                    {existingTargetedKeywords?.total_keywords > 0 && (
                      <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                        <h5 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                          Existing Targeted Keywords ({existingTargetedKeywords.total_keywords} total)
                        </h5>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {existingTargetedKeywords.campaigns?.map((camp, idx) => (
                            <div key={idx} className="flex justify-between items-center p-2 bg-white rounded border text-sm">
                              <div>
                                <span className="font-medium text-slate-800">{camp}</span>
                                <span className="text-slate-400 ml-2 text-xs">
                                  ({existingTargetedKeywords.keywords_by_campaign?.[camp]?.length || 0} keywords)
                                </span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                                onClick={() => handleDeleteTargetedKeywords(camp)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Admin-only Settings - Super Admin Only */}
          {isMasterAdmin && (
            <Card className="chart-container mt-6 border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Advanced Settings</CardTitle>
                <CardDescription>Admin-only configuration options</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Country Traffic Exclusion */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800 mb-3">Traffic Filters</h4>
                    <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-amber-900">Exclude Turkey Traffic</p>
                        <p className="text-xs text-amber-700">
                          Hide contacts and data from Turkey/Türkiye in analytics
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={displaySettings.exclude_turkey_traffic || false}
                          onChange={toggleTurkeyTraffic}
                          disabled={savingDisplaySettings}
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                      </label>
                    </div>
                  </div>

                  {/* Traffic Data Source Toggle - Super Admin Only */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-blue-900">Website Traffic Data Source</p>
                        <p className="text-xs text-blue-700">
                          Choose between GA4 or HubSpot for Website Traffic page analytics
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={async () => {
                            setSavingDisplaySettings(true);
                            try {
                              const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
                              await axios.put(`${API}/tenants/${viewingTenantId}/display-settings`, 
                                { traffic_data_source: "ga4" },
                                { params: { Authorization: `Bearer ${sessionToken}` } }
                              );
                              setDisplaySettings({ ...displaySettings, traffic_data_source: "ga4" });
                              toast.success("Traffic data source set to GA4");
                            } catch (error) {
                              console.error("Error updating traffic source:", error);
                              toast.error("Failed to update setting");
                            } finally {
                              setSavingDisplaySettings(false);
                            }
                          }}
                          disabled={savingDisplaySettings}
                          className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                            displaySettings.traffic_data_source === "ga4" || !displaySettings.traffic_data_source
                              ? "bg-blue-600 text-white shadow-md"
                              : "bg-white text-blue-700 border border-blue-300 hover:bg-blue-50"
                          }`}
                        >
                          Google Analytics (GA4)
                        </button>
                        <button
                          onClick={async () => {
                            setSavingDisplaySettings(true);
                            try {
                              const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
                              await axios.put(`${API}/tenants/${viewingTenantId}/display-settings`, 
                                { traffic_data_source: "hubspot" },
                                { params: { Authorization: `Bearer ${sessionToken}` } }
                              );
                              setDisplaySettings({ ...displaySettings, traffic_data_source: "hubspot" });
                              toast.success("Traffic data source set to HubSpot");
                            } catch (error) {
                              console.error("Error updating traffic source:", error);
                              toast.error("Failed to update setting");
                            } finally {
                              setSavingDisplaySettings(false);
                            }
                          }}
                          disabled={savingDisplaySettings}
                          className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                            displaySettings.traffic_data_source === "hubspot"
                              ? "bg-orange-500 text-white shadow-md"
                              : "bg-white text-orange-700 border border-orange-300 hover:bg-orange-50"
                          }`}
                        >
                          HubSpot Traffic
                        </button>
                      </div>
                      <p className="text-xs text-blue-600">
                        Current: <strong>{displaySettings.traffic_data_source === "hubspot" ? "HubSpot" : "GA4"}</strong>
                        {displaySettings.traffic_data_source === "hubspot" && " (Requires HubSpot connection with business-intelligence scope)"}
                      </p>
                    </div>
                  </div>

                  {/* ICP Button Toggle - Super Admin Only */}
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-900">Show Assess ICP Button</p>
                        <p className="text-xs text-green-700">
                          Display the ICP evaluation button on Form Submissions page
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={displaySettings.show_icp_button !== false}
                          onChange={async () => {
                            const newValue = !displaySettings.show_icp_button;
                            setSavingDisplaySettings(true);
                            try {
                              const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
                              await axios.put(`${API}/tenants/${viewingTenantId}/display-settings`, 
                                { show_icp_button: newValue },
                                { params: { Authorization: `Bearer ${sessionToken}` } }
                              );
                              setDisplaySettings({ ...displaySettings, show_icp_button: newValue });
                              toast.success(newValue ? "ICP button enabled" : "ICP button disabled");
                            } catch (error) {
                              console.error("Error toggling ICP button:", error);
                              toast.error("Failed to update setting");
                            } finally {
                              setSavingDisplaySettings(false);
                            }
                          }}
                          disabled={savingDisplaySettings}
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                      </label>
                    </div>
                  </div>

                  {/* True AI Referrals Card Toggle - Super Admin Only */}
                  <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-cyan-900">Show True AI Referrals Card</p>
                        <p className="text-xs text-cyan-700">
                          Display the True AI Referrals card on Website Traffic page
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={displaySettings.show_true_ai_referrals !== false}
                          onChange={async () => {
                            const newValue = displaySettings.show_true_ai_referrals === false;
                            setSavingDisplaySettings(true);
                            try {
                              const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
                              await axios.put(`${API}/tenants/${viewingTenantId}/display-settings`, 
                                { show_true_ai_referrals: newValue },
                                { params: { Authorization: `Bearer ${sessionToken}` } }
                              );
                              setDisplaySettings({ ...displaySettings, show_true_ai_referrals: newValue });
                              toast.success(newValue ? "True AI Referrals card shown" : "True AI Referrals card hidden");
                            } catch (error) {
                              console.error("Error toggling True AI Referrals:", error);
                              toast.error("Failed to update setting");
                            } finally {
                              setSavingDisplaySettings(false);
                            }
                          }}
                          disabled={savingDisplaySettings}
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
                      </label>
                    </div>

                    {/* Turkey Exclusion Alert Toggle */}
                    <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-100">
                      <div>
                        <p className="text-sm font-medium text-red-900">Show Turkey Exclusion Alert</p>
                        <p className="text-xs text-red-600">
                          Display the "Turkey traffic excluded" alert banner on Website Traffic page
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={displaySettings.show_turkey_exclusion_alert !== false}
                          onChange={async () => {
                            const newValue = displaySettings.show_turkey_exclusion_alert === false;
                            setSavingDisplaySettings(true);
                            try {
                              const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
                              await axios.put(`${API}/tenants/${viewingTenantId}/display-settings`, 
                                { show_turkey_exclusion_alert: newValue },
                                { params: { Authorization: `Bearer ${sessionToken}` } }
                              );
                              setDisplaySettings({ ...displaySettings, show_turkey_exclusion_alert: newValue });
                              toast.success(newValue ? "Turkey exclusion alert shown" : "Turkey exclusion alert hidden");
                            } catch (error) {
                              console.error("Error toggling Turkey exclusion alert:", error);
                              toast.error("Failed to update setting");
                            } finally {
                              setSavingDisplaySettings(false);
                            }
                          }}
                          disabled={savingDisplaySettings}
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Branding Tab - Super Admin or Branding Admin Only */}
        {(isSuperAdmin || user?.role === 'branding_admin') && (
        <TabsContent value="branding">
          <Card className="chart-container">
            <CardHeader>
              <CardTitle>Instance Branding</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Instance Name</Label>
                  <Input
                    value={settings?.platform_name || ""}
                    onChange={(e) =>
                      setSettings({ ...settings, platform_name: e.target.value })
                    }
                    placeholder="e.g., James Hardie Analytics"
                    data-testid="platform-name-input"
                    disabled={!canChangeInstanceName}
                  />
                  <p className="text-xs text-slate-500">
                    {canChangeInstanceName 
                      ? "Appears in the sidebar header" 
                      : "Only Jason and JCI Marketing admins can change this"}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={settings?.company_name || ""}
                    onChange={(e) =>
                      setSettings({ ...settings, company_name: e.target.value })
                    }
                    placeholder="e.g., James Hardie"
                  />
                  <p className="text-xs text-slate-500">Used in reports</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Company Logo</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-slate-300 transition-colors">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label htmlFor="logo-upload" className="cursor-pointer">
                    {uploadingLogo ? (
                      <RefreshCw className="w-8 h-8 text-slate-400 mx-auto mb-2 animate-spin" />
                    ) : (logoPreview || settings?.company_logo) ? (
                      <img 
                        src={logoPreview || settings?.company_logo} 
                        alt="Company Logo" 
                        className="max-h-16 mx-auto mb-2 object-contain"
                      />
                    ) : (
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    )}
                    <p className="text-sm text-slate-500">
                      {uploadingLogo ? "Uploading..." : (logoPreview || settings?.company_logo) ? "Click to change logo" : "Click to upload logo"}
                    </p>
                    <p className="text-xs text-slate-400">PNG, JPG up to 2MB</p>
                  </label>
                </div>
                {(logoPreview || settings?.company_logo) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={async () => {
                      const tenantId = sessionStorage.getItem("viewing_tenant_id");
                      if (!tenantId) {
                        toast.error("No tenant context");
                        return;
                      }
                      try {
                        await axios.delete(`${API}/tenants/${tenantId}/branding/logo`, {
                          params: { Authorization: `Bearer ${sessionToken}` }
                        });
                        setLogoPreview(null);
                        setSettings({ ...settings, company_logo: null });
                        toast.success("Logo deleted");
                        refreshBranding();
                      } catch (error) {
                        toast.error("Failed to delete logo");
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Logo
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Primary Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings?.default_brand_colors?.primary || "#00A5A8"}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          default_brand_colors: {
                            ...settings?.default_brand_colors,
                            primary: e.target.value,
                          },
                        })
                      }
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings?.default_brand_colors?.primary || "#00A5A8"}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          default_brand_colors: {
                            ...settings?.default_brand_colors,
                            primary: e.target.value,
                          },
                        })
                      }
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Secondary Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings?.default_brand_colors?.secondary || "#0F172A"}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          default_brand_colors: {
                            ...settings?.default_brand_colors,
                            secondary: e.target.value,
                          },
                        })
                      }
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings?.default_brand_colors?.secondary || "#0F172A"}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          default_brand_colors: {
                            ...settings?.default_brand_colors,
                            secondary: e.target.value,
                          },
                        })
                      }
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium mb-2">Contrast Check</p>
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: settings?.default_brand_colors?.primary || "#00A5A8" }}
                  >
                    Aa
                  </div>
                  <div>
                    <p className="text-sm">
                      Primary on White:{" "}
                      <span
                        className={
                          parseFloat(getContrastRatio(settings?.default_brand_colors?.primary || "#00A5A8", "#FFFFFF")) >= 4.5
                            ? "text-emerald-600"
                            : "text-red-500"
                        }
                      >
                        {getContrastRatio(settings?.default_brand_colors?.primary || "#00A5A8", "#FFFFFF")}:1
                      </span>
                    </p>
                    <p className="text-xs text-slate-500">
                      {parseFloat(getContrastRatio(settings?.default_brand_colors?.primary || "#00A5A8", "#FFFFFF")) >= 4.5
                        ? "✓ Passes WCAG AA"
                        : "✗ Needs improvement"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Chart Color Palette</Label>
                <p className="text-xs text-slate-500 mb-2">First two colors sync from Primary and Secondary. Customize additional accent colors below.</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* First two colors are locked to primary/secondary */}
                  <div className="relative">
                    <div 
                      className="w-10 h-10 rounded border-2 border-dashed border-slate-300"
                      style={{ backgroundColor: settings?.default_brand_colors?.primary || "#00A5A8" }}
                      title="Synced from Primary Color"
                    />
                    <span className="absolute -bottom-4 left-0 text-[10px] text-slate-400">Primary</span>
                  </div>
                  <div className="relative">
                    <div 
                      className="w-10 h-10 rounded border-2 border-dashed border-slate-300"
                      style={{ backgroundColor: settings?.default_brand_colors?.secondary || "#0F172A" }}
                      title="Synced from Secondary Color"
                    />
                    <span className="absolute -bottom-4 left-0 text-[10px] text-slate-400">Secondary</span>
                  </div>
                  {/* Additional customizable colors (3-5) */}
                  {[2, 3, 4].map((index) => (
                    <input
                      key={index}
                      type="color"
                      value={(settings?.default_brand_colors?.chart_colors || ["#00A5A8", "#0F172A", "#0EA5E9", "#64748B", "#10B981"])[index] || "#64748B"}
                      onChange={(e) => {
                        const primary = settings?.default_brand_colors?.primary || "#00A5A8";
                        const secondary = settings?.default_brand_colors?.secondary || "#0F172A";
                        const currentColors = settings?.default_brand_colors?.chart_colors || ["#00A5A8", "#0F172A", "#0EA5E9", "#64748B", "#10B981"];
                        const newColors = [primary, secondary, ...currentColors.slice(2)];
                        newColors[index] = e.target.value;
                        setSettings({
                          ...settings,
                          default_brand_colors: {
                            ...settings?.default_brand_colors,
                            chart_colors: newColors,
                          },
                        });
                      }}
                      className="w-10 h-10 rounded cursor-pointer"
                      title={`Accent Color ${index - 1}`}
                    />
                  ))}
                </div>
              </div>

              <Button onClick={saveSettings} disabled={saving} className="btn-primary">
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Branding
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="chart-container" data-testid="users-table-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>User Management</CardTitle>
                {isMasterAdmin && (
                  <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary gap-2">
                        <UserPlus className="w-4 h-4" />
                        Add User
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New User</DialogTitle>
                        <DialogDescription>
                          Create a new user account. Set a password for email login, or leave blank for OAuth only.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Name</Label>
                          <Input
                            id="name"
                            placeholder="John Doe"
                            value={newUserData.name}
                            onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="john@example.com"
                            value={newUserData.email}
                            onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="password">Password (optional)</Label>
                          <Input
                            id="password"
                            type="password"
                            placeholder="Leave blank for OAuth only"
                            value={newUserData.password}
                            onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                          />
                          <p className="text-xs text-slate-500">Min 6 characters. If set, user can login with email/password.</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="role">Role</Label>
                          <Select
                            value={newUserData.role}
                            onValueChange={(value) => setNewUserData({ ...newUserData, role: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="master_admin">Master Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddUserDialog(false)}>
                          Cancel
                        </Button>
                        <Button onClick={addUser} disabled={addingUser} className="btn-primary">
                          {addingUser ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                          Add User
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    {isMasterAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-slate-500">{u.email}</TableCell>
                      <TableCell>{getRoleBadge(u.role)}</TableCell>
                      <TableCell>
                        <Badge className={u.is_active !== false ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                          {u.is_active !== false ? "Active" : "Suspended"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      {isMasterAdmin && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Select
                              value={u.role}
                              onValueChange={(value) => updateUserRole(u.user_id, value)}
                              disabled={MASTER_ADMIN_EMAILS.includes(u.email)}
                            >
                              <SelectTrigger className="w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="master_admin">Master Admin</SelectItem>
                              </SelectContent>
                            </Select>
                            
                            {!MASTER_ADMIN_EMAILS.includes(u.email) && u.email !== user?.email && (
                              <>
                                {u.is_active !== false ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => suspendUser(u.user_id, u.name)}
                                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                    title="Suspend user"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => activateUser(u.user_id)}
                                    className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                    title="Activate user"
                                  >
                                    <UserCheck className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => deleteUser(u.user_id, u.name)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Delete user"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {users.length === 0 && (
                <p className="text-center text-slate-500 py-8">No users found</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features Tab - Super Admin Only */}
        {isSuperAdmin && (
        <TabsContent value="features">
          <Card className="chart-container">
            <CardHeader>
              <CardTitle>Feature Toggles</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">AI Chat</p>
                    <p className="text-sm text-slate-500">Enable AI-powered chat assistant</p>
                  </div>
                  <Switch
                    checked={settings?.features?.ai_chat ?? true}
                    onCheckedChange={(checked) =>
                      setSettings({
                        ...settings,
                        features: { ...settings?.features, ai_chat: checked },
                      })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">ICP Matching</p>
                    <p className="text-sm text-slate-500">AI-powered lead qualification with web search</p>
                  </div>
                  <Switch
                    checked={settings?.features?.icp_matching ?? true}
                    onCheckedChange={(checked) =>
                      setSettings({
                        ...settings,
                        features: { ...settings?.features, icp_matching: checked },
                      })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">PDF Export</p>
                    <p className="text-sm text-slate-500">Allow exporting reports to PDF</p>
                  </div>
                  <Switch
                    checked={settings?.features?.pdf_export ?? true}
                    onCheckedChange={(checked) =>
                      setSettings({
                        ...settings,
                        features: { ...settings?.features, pdf_export: checked },
                      })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">PPTX Export</p>
                    <p className="text-sm text-slate-500">Allow exporting reports to PowerPoint</p>
                  </div>
                  <Switch
                    checked={settings?.features?.pptx_export ?? true}
                    onCheckedChange={(checked) =>
                      setSettings({
                        ...settings,
                        features: { ...settings?.features, pptx_export: checked },
                      })
                    }
                  />
                </div>
              </div>

              {/* Social Insights Visibility Controls - Super Admin Only */}
              {isSuperAdmin && (
                <>
                  <hr className="my-6 border-slate-200" />
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-900">Social Insights Visibility (Tenant-specific)</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Control which sections are visible on the Social Insights page for this tenant.
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Page Views Chart</p>
                        <p className="text-sm text-slate-500">Show page views by section chart</p>
                      </div>
                      <Switch
                        checked={settings?.social_insights?.show_page_views_chart ?? true}
                        onCheckedChange={(checked) =>
                          setSettings({
                            ...settings,
                            social_insights: { ...settings?.social_insights, show_page_views_chart: checked },
                          })
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Engagement Chart</p>
                        <p className="text-sm text-slate-500">Show engagement breakdown chart</p>
                      </div>
                      <Switch
                        checked={settings?.social_insights?.show_engagement_chart ?? true}
                        onCheckedChange={(checked) =>
                          setSettings({
                            ...settings,
                            social_insights: { ...settings?.social_insights, show_engagement_chart: checked },
                          })
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Page Views Table</p>
                        <p className="text-sm text-slate-500">Show detailed page views stats table</p>
                      </div>
                      <Switch
                        checked={settings?.social_insights?.show_page_views_table ?? true}
                        onCheckedChange={(checked) =>
                          setSettings({
                            ...settings,
                            social_insights: { ...settings?.social_insights, show_page_views_table: checked },
                          })
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Additional Metrics</p>
                        <p className="text-sm text-slate-500">Show engagement stats and additional metrics tables</p>
                      </div>
                      <Switch
                        checked={settings?.social_insights?.show_additional_metrics ?? true}
                        onCheckedChange={(checked) =>
                          setSettings({
                            ...settings,
                            social_insights: { ...settings?.social_insights, show_additional_metrics: checked },
                          })
                        }
                      />
                    </div>
                  </div>
                </>
              )}

              {/* ICP Column Settings */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold mb-3">ICP Settings</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Show ICP Column</p>
                      <p className="text-sm text-slate-500">Display ICP status column in Form Submissions and other tables</p>
                    </div>
                    <Switch
                      checked={settings?.show_icp_column ?? true}
                      onCheckedChange={(checked) =>
                        setSettings({
                          ...settings,
                          show_icp_column: checked,
                        })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Show ICP Match Button</p>
                      <p className="text-sm text-slate-500">Show the ICP Match/Unmatch action buttons</p>
                    </div>
                    <Switch
                      checked={settings?.show_icp_button ?? true}
                      onCheckedChange={(checked) =>
                        setSettings({
                          ...settings,
                          show_icp_button: checked,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <Button onClick={saveSettings} disabled={saving} className="btn-primary">
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Stage Mapping Tab - Map HubSpot lifecycle stages to MQL/SQL */}
        <TabsContent value="stages">
          <div className="space-y-6">
            {/* HubSpot Contact Summary */}
            {contactsSummary && (
              <Card className="chart-container border-blue-200 bg-blue-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <CheckCircle2 className="w-5 h-5" />
                    HubSpot Contacts Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6 flex-wrap">
                    <div>
                      <p className="text-2xl font-bold text-blue-900">{contactsSummary.total?.toLocaleString() || 0}</p>
                      <p className="text-sm text-blue-600">Total Contacts Synced</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {contactsSummary.by_stage?.slice(0, 6).map(({ stage, count }) => (
                        <Badge key={stage} variant="outline" className="bg-white text-blue-700 border-blue-300">
                          {stage}: {count.toLocaleString()}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3 text-blue-600 border-blue-300 hover:bg-blue-100"
                    onClick={fetchLifecycleStages}
                    disabled={loadingStages}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingStages ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Available Lifecycle Stages from HubSpot */}
            {lifecycleStages.length > 0 && (
              <Card className="chart-container">
                <CardHeader>
                  <CardTitle className="text-base">Available HubSpot Lifecycle Stages</CardTitle>
                  <CardDescription>
                    Click on a stage to copy it to your clipboard for mapping below
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {lifecycleStages.map((stage) => (
                      <Badge 
                        key={stage.value || stage.label} 
                        variant="outline" 
                        className="cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => {
                          // Copy the label (human-readable name) for use in mapping
                          navigator.clipboard.writeText(stage.label);
                          toast.success(`Copied: ${stage.label}`);
                        }}
                      >
                        {stage.label}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="chart-container">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#00A5A8]" />
                  HubSpot Lifecycle Stage Mapping
                </CardTitle>
                <CardDescription>
                  Map your HubSpot lifecycle stages to determine which contacts appear on the MQLs and SQLs pages
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* MQL Stages */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      MQL Stages
                    </Label>
                    <p className="text-sm text-slate-500">
                      Contacts with these HubSpot lifecycle stages will appear on the MQLs page
                    </p>
                    <Textarea
                      placeholder="Enter stage names (comma or newline separated):
MQL, marketingqualifiedlead, Marketing Qualified Lead"
                      rows={4}
                      className="font-mono text-sm"
                      value={mqlStagesText}
                      onChange={(e) => setMqlStagesText(e.target.value)}
                    />
                  </div>

                  {/* SQL Stages */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      SQL Stages
                    </Label>
                    <p className="text-sm text-slate-500">
                      Contacts with these HubSpot lifecycle stages will appear on the SQLs page
                    </p>
                    <Textarea
                      placeholder="Enter stage names (comma or newline separated):
SQL, salesqualifiedlead, Sales Qualified Lead"
                      rows={4}
                      className="font-mono text-sm"
                      value={sqlStagesText}
                      onChange={(e) => setSqlStagesText(e.target.value)}
                    />
                  </div>
                </div>

                {/* Closed Won Stages - Full Width */}
                <div className="space-y-3 mt-6">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    Closed Won Deal Stages
                  </Label>
                  <p className="text-sm text-slate-500">
                    Deals with these HubSpot deal stages will count as &quot;Closed Won&quot; on the Deals page and Dashboard
                  </p>
                  <Textarea
                    placeholder="Enter deal stage names (comma or newline separated):
closedwon, Closed Won, Won, Deal Won"
                    rows={3}
                    className="font-mono text-sm"
                    value={closedWonStagesText}
                    onChange={(e) => setClosedWonStagesText(e.target.value)}
                  />
                </div>

                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <p className="text-sm text-slate-600">
                    <strong>Tip:</strong> Click on any stage below to copy its name. Stage matching is case-insensitive.
                  </p>
                </div>

                {/* Available Lifecycle Stages */}
                {lifecycleStages && lifecycleStages.length > 0 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-semibold text-blue-800 mb-3">
                      Available Lifecycle Stages (click to copy):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {lifecycleStages.map((stage) => (
                        <button
                          key={stage.value || stage.label}
                          onClick={() => {
                            // Copy the LABEL (human-readable name) for use in mapping
                            navigator.clipboard.writeText(stage.label);
                            toast.success(`Copied "${stage.label}" to clipboard`);
                          }}
                          className="px-3 py-1.5 bg-white border border-blue-300 rounded-lg text-sm hover:bg-blue-100 transition-colors cursor-pointer"
                          title={`Click to copy: ${stage.label}`}
                        >
                          <span className="font-medium text-blue-900">{stage.label || stage.value}</span>
                          {stage.value && stage.value !== stage.label && (
                            <span className="text-blue-500 ml-1 text-xs">(ID: {stage.value})</span>
                          )}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      Click on a stage to copy the label. You can use either the label name or numeric ID in your mapping.
                    </p>
                  </div>
                )}

                <Button onClick={saveSettings} disabled={saving} className="btn-primary">
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Stage Mapping
                </Button>
              </CardContent>
            </Card>

            {/* Deal Stage Probabilities */}
            <Card className="chart-container mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#00A5A8]" />
                  Deal Stage Probabilities
                </CardTitle>
                <CardDescription>
                  Assign win probability percentages to each deal stage for weighted pipeline forecasting
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {loadingDealStages ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : dealStages.length === 0 ? (
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-sm text-slate-600">
                      No deal stages found. Please sync your HubSpot deals first from the Integrations tab.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {dealStages.map((stage) => (
                        <div key={stage.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate" title={stage.label}>
                              {stage.label}
                            </p>
                            {stage.pipeline && (
                              <p className="text-xs text-slate-400 truncate" title={stage.pipeline}>
                                {stage.pipeline}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={dealStageProbabilities[stage.id] ?? ''}
                              onChange={(e) => updateProbability(stage.id, e.target.value)}
                              className="w-20 text-center"
                              placeholder="—"
                            />
                            <span className="text-sm text-slate-500">%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mt-4">
                      <p className="text-sm text-amber-700">
                        <strong>Tip:</strong> Set Closed Won to 100% and Closed Lost to 0%. Other stages should reflect historical conversion rates.
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <Button 
                        onClick={saveDealStageProbabilities} 
                        disabled={savingProbabilities} 
                        className="btn-primary"
                      >
                        {savingProbabilities ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save Probabilities
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={fetchDealStages}
                        disabled={loadingDealStages}
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loadingDealStages ? 'animate-spin' : ''}`} />
                        Refresh Stages
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Page Titles Configuration - Super Admin Only */}
            {isSuperAdmin && (
              <Card className="chart-container mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#00A5A8]" />
                    Page Titles
                  </CardTitle>
                  <CardDescription>
                    Customize the display names of analytics pages for this tenant
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>MQLs Page Title</Label>
                      <Input
                        value={displaySettings?.page_titles?.mqls || "MQLs"}
                        onChange={(e) => setDisplaySettings({
                          ...displaySettings,
                          page_titles: {
                            ...displaySettings?.page_titles,
                            mqls: e.target.value
                          }
                        })}
                        placeholder="MQLs"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>SQLs Page Title</Label>
                      <Input
                        value={displaySettings?.page_titles?.sqls || "SQLs"}
                        onChange={(e) => setDisplaySettings({
                          ...displaySettings,
                          page_titles: {
                            ...displaySettings?.page_titles,
                            sqls: e.target.value
                          }
                        })}
                        placeholder="SQLs"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Deals Page Title</Label>
                      <Input
                        value={displaySettings?.page_titles?.deals || "Deals"}
                        onChange={(e) => setDisplaySettings({
                          ...displaySettings,
                          page_titles: {
                            ...displaySettings?.page_titles,
                            deals: e.target.value
                          }
                        })}
                        placeholder="Deals"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Form Submissions Page Title</Label>
                      <Input
                        value={displaySettings?.page_titles?.form_submissions || "Form Submissions"}
                        onChange={(e) => setDisplaySettings({
                          ...displaySettings,
                          page_titles: {
                            ...displaySettings?.page_titles,
                            form_submissions: e.target.value
                          }
                        })}
                        placeholder="Form Submissions"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>For Heath Page Title</Label>
                      <Input
                        value={displaySettings?.page_titles?.for_heath || "For Heath"}
                        onChange={(e) => setDisplaySettings({
                          ...displaySettings,
                          page_titles: {
                            ...displaySettings?.page_titles,
                            for_heath: e.target.value
                          }
                        })}
                        placeholder="For Heath"
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={() => saveDisplaySettings(displaySettings)} 
                    disabled={savingDisplaySettings} 
                    className="btn-primary"
                  >
                    {savingDisplaySettings ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Page Titles
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Goals Tab - Tenant Goal Setting */}
        <TabsContent value="goals">
          <Card className="chart-container">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-[#00A5A8]" />
                Marketing Goals
              </CardTitle>
              <CardDescription>
                Set monthly targets for your marketing metrics. AI will track progress and suggest interventions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add Goal Button */}
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-600">
                  Goals are validated against your historical performance to ensure they are realistic.
                </p>
                <Dialog open={showAddGoalDialog} onOpenChange={setShowAddGoalDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary">
                      <Target className="w-4 h-4 mr-2" />
                      Add Goal
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Goal</DialogTitle>
                      <DialogDescription>
                        Set a target for your marketing metrics. Goals are validated against historical data.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Metric</Label>
                        <Select
                          value={newGoalData.metric}
                          onValueChange={(value) => setNewGoalData({ ...newGoalData, metric: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select metric" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mqls">MQLs</SelectItem>
                            <SelectItem value="sqls">SQLs</SelectItem>
                            <SelectItem value="website_sessions">Website Sessions</SelectItem>
                            <SelectItem value="pipeline_value">Pipeline Value ($)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Target Value</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 80"
                          value={newGoalData.target_value}
                          onChange={(e) => setNewGoalData({ ...newGoalData, target_value: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Period</Label>
                        <Select
                          value={newGoalData.period}
                          onValueChange={(value) => setNewGoalData({ ...newGoalData, period: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select period" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddGoalDialog(false)}>
                        Cancel
                      </Button>
                      <Button
                        className="btn-primary"
                        disabled={savingGoal || !newGoalData.target_value}
                        onClick={async () => {
                          setSavingGoal(true);
                          try {
                            const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
                            await axios.post(
                              `${API}/ai-analytics/goals?X-Tenant-ID=${viewingTenantId}&Authorization=${sessionToken}`,
                              {
                                metric: newGoalData.metric,
                                target_value: parseInt(newGoalData.target_value),
                                period: newGoalData.period
                              }
                            );
                            toast.success("Goal created successfully");
                            setShowAddGoalDialog(false);
                            setNewGoalData({ metric: "mqls", target_value: "", period: "monthly" });
                            // Refresh goals
                            fetchGoals();
                          } catch (error) {
                            console.error("Error creating goal:", error);
                            toast.error(error.response?.data?.detail || "Failed to create goal");
                          } finally {
                            setSavingGoal(false);
                          }
                        }}
                      >
                        {savingGoal ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Create Goal
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Goals List */}
              {loadingGoals ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  <p className="text-sm text-slate-500 mt-2">Loading goals...</p>
                </div>
              ) : goals.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No goals set yet</p>
                  <p className="text-sm mt-1">Click Add Goal to create your first marketing goal</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {goals.map((goal) => (
                    <div
                      key={goal.goal_id}
                      className={`p-4 rounded-lg border ${
                        goal.status === "pending_approval" 
                          ? "bg-amber-50 border-amber-200" 
                          : goal.status === "active"
                          ? "bg-green-50 border-green-200"
                          : "bg-slate-50 border-slate-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold capitalize">
                              {goal.metric?.replace(/_/g, " ")}
                            </span>
                            <Badge variant={goal.status === "active" ? "default" : "secondary"}>
                              {goal.status}
                            </Badge>
                            {goal.admin_approved && (
                              <Badge variant="outline" className="text-green-600 border-green-300">
                                Approved
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 mt-1">
                            Target: {goal.target_value?.toLocaleString()} per {goal.period}
                          </p>
                          {goal.validation && (
                            <p className="text-xs text-slate-500 mt-1">
                              Historical max: {goal.validation.historical_max?.toLocaleString()} | 
                              {goal.validation.is_realistic ? " Goal is realistic" : " Goal may be ambitious"}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {goal.status === "pending_approval" && (
                            <Button
                              size="sm"
                              onClick={async () => {
                                try {
                                  const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
                                  await axios.patch(
                                    `${API}/ai-analytics/goals/${goal.goal_id}?X-Tenant-ID=${viewingTenantId}&Authorization=${sessionToken}`,
                                    { admin_approved: true }
                                  );
                                  toast.success("Goal approved");
                                  fetchGoals();
                                } catch (error) {
                                  toast.error("Failed to approve goal");
                                }
                              }}
                            >
                              Approve
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={async () => {
                              try {
                                const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
                                await axios.delete(
                                  `${API}/ai-analytics/goals/${goal.goal_id}?X-Tenant-ID=${viewingTenantId}&Authorization=${sessionToken}`
                                );
                                toast.success("Goal deleted");
                                fetchGoals();
                              } catch (error) {
                                toast.error("Failed to delete goal");
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Info Box */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>How Goals Work:</strong> Goals are validated against your historical data. 
                  If a goal exceeds 1.5x your historical max, it requires admin approval. 
                  The AI will track progress and suggest interventions when you fall behind pace.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ICP Tab - Ideal Customer Profile Configuration */}
        <TabsContent value="icp">
          <Card className="chart-container">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-[#00A5A8]" />
                Ideal Customer Profile (ICP)
              </CardTitle>
              <CardDescription>
                Define your ideal customer criteria for AI-powered lead evaluation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Form Submissions ICP */}
              <div className="space-y-2">
                <Label htmlFor="icp-text" className="text-base font-semibold flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  Form Submissions / Contacts ICP
                </Label>
                <Textarea
                  id="icp-text"
                  placeholder="Describe your ideal customer profile for form leads. Include criteria such as:

- Industry: SaaS, Manufacturing, Healthcare, Retail
- Company size: 50-500 employees, $10M-$100M revenue
- Job titles: VP of Marketing, CMO, Director of Growth, Marketing Manager
- Pain points: Need better analytics, struggling with attribution
- Tech stack: Using HubSpot, Salesforce, Marketo
- Geography: North America, EMEA, specific states/countries"
                  rows={8}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500">
                  This criteria will be used by AI to evaluate leads from form submissions.
                </p>
              </div>

              {/* Deals ICP */}
              <div className="space-y-2">
                <Label htmlFor="deals-icp-text" className="text-base font-semibold flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  Deals ICP
                </Label>
                <Textarea
                  id="deals-icp-text"
                  placeholder="Describe your ideal customer profile for deals. Include criteria such as:

- Deal size: $50K-$500K ARR
- Sales cycle: 30-90 days
- Decision makers: C-Suite involvement required
- Budget indicators: Approved budget, fiscal year timing
- Company characteristics: Growth stage, funding status
- Competition: Replacing competitor, greenfield opportunity"
                  rows={8}
                  className="font-mono text-sm"
                  value={dealsIcpCriteria}
                  onChange={(e) => setDealsIcpCriteria(e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  This criteria will be used by AI to evaluate deals and opportunities.
                </p>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>How it works:</strong> When you click &quot;Assess ICP Fit&quot; on a form submission or deal, 
                  our AI will compare the lead&apos;s company information against these criteria using web research 
                  to provide a fit score and analysis.
                </p>
              </div>
              <Button onClick={saveSettings} disabled={saving} className="btn-primary">
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save ICP Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab - JCI Domain Only */}
        {canSeeNotes && (
          <TabsContent value="notes">
            <div className="space-y-6">
              {/* Outgoing Webhook Secret */}
              <Card className="chart-container border-emerald-200 bg-emerald-50/30">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Link2 className="w-5 h-5 text-emerald-600" />
                        Your Webhook Secret
                      </CardTitle>
                      <CardDescription>
                        Copy this secret and use it to configure your receiving endpoint in j-MAP
                      </CardDescription>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">Outgoing Webhook</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingSecret ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  ) : webhookSecret ? (
                    <>
                      <div className="flex items-center gap-2">
                        <Input
                          value={webhookSecret}
                          readOnly
                          className="font-mono text-sm bg-white"
                        />
                        <Button 
                          onClick={copySecretToClipboard}
                          variant="outline"
                          className={copiedSecret ? "bg-emerald-100 text-emerald-700" : ""}
                        >
                          {copiedSecret ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : (
                            <ExternalLink className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <div className="bg-white p-3 rounded-lg border text-sm text-slate-600 space-y-2">
                        <p className="font-medium">How to use:</p>
                        <ol className="list-decimal list-inside space-y-1 text-xs">
                          <li>Copy the webhook secret above</li>
                          <li>In j-MAP, create an endpoint like: <code className="bg-slate-100 px-1 rounded">/webhook/{"{secret}"}</code></li>
                          <li>Paste your full j-MAP endpoint URL below in the &quot;Destination URL&quot; field</li>
                          <li>j-MAP Analytics will POST new competitor ad data to that URL</li>
                        </ol>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-slate-500">Unable to load webhook secret</p>
                  )}
                </CardContent>
              </Card>

              {/* Webhook Destination Configuration */}
              <Card className="chart-container border-purple-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Link2 className="w-5 h-5 text-purple-600" />
                        Webhook Destination
                      </CardTitle>
                      <CardDescription>
                        Where should j-MAP Analytics send new competitor ad notifications?
                      </CardDescription>
                    </div>
                    <Badge className="bg-purple-100 text-purple-700">JCI Internal</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingWebhook ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="webhook_url">Destination URL (your j-MAP endpoint)</Label>
                        <Input
                          id="webhook_url"
                          type="url"
                          placeholder="https://jmap.emergentagent.com/api/webhook/competitor-ads"
                          value={webhookSettings.webhook_url}
                          onChange={(e) => setWebhookSettings({ ...webhookSettings, webhook_url: e.target.value })}
                          className="font-mono text-sm"
                        />
                        <p className="text-xs text-slate-500">
                          When new competitor ads are detected, a POST request with ad data will be sent here.
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Competitors to Monitor (up to 3)</Label>
                        <div className="space-y-2">
                          {webhookSettings.competitors.map((competitor, index) => (
                            <Input
                              key={index}
                              placeholder={`Competitor ${index + 1} name (exact match)`}
                              value={competitor}
                              onChange={(e) => {
                                const newCompetitors = [...webhookSettings.competitors];
                                newCompetitors[index] = e.target.value;
                                setWebhookSettings({ ...webhookSettings, competitors: newCompetitors });
                              }}
                            />
                          ))}
                        </div>
                        <p className="text-xs text-slate-500">
                          Enter exact advertiser names as they appear on LinkedIn Ad Library.
                        </p>
                      </div>

                      <div className="bg-slate-50 p-3 rounded-lg text-xs text-slate-600 space-y-1">
                        <p className="font-medium">Webhook Payload Format:</p>
                        <pre className="bg-slate-100 p-2 rounded overflow-x-auto">
{`{
  "event": "new_competitor_ads",
  "timestamp": "2026-01-07T...",
  "count": 2,
  "ads": [
    {
      "competitor_name": "...",
      "ad_url": "...",
      "impressions": {...},
      "countries": [...]
    }
  ]
}`}
                        </pre>
                      </div>

                      <Button onClick={saveWebhookSettings} disabled={savingWebhook} className="btn-primary">
                        {savingWebhook ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save Webhook Settings
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Development Notes */}
              <Card className="chart-container">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Development Notes
                      </CardTitle>
                      <CardDescription>
                        Internal notes for JCI team members only
                      </CardDescription>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">JCI Internal</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingNotes ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : (
                    <>
                      <Textarea
                        value={devNotes}
                        onChange={(e) => setDevNotes(e.target.value)}
                        placeholder="Enter development notes here... (Markdown supported)"
                        className="min-h-[300px] font-mono text-sm"
                      />
                      <div className="flex justify-between items-center">
                        <p className="text-xs text-slate-500">
                          Supports Markdown formatting
                        </p>
                        <Button onClick={saveDevNotes} disabled={savingNotes} className="btn-primary">
                          {savingNotes ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                          Save Notes
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {/* Database Backup Tab - Super Admin Only */}
        {isSuperAdmin && (
          <TabsContent value="database">
            <DatabaseBackup sessionToken={sessionToken} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

// Database Backup Component for Super Admins
const DatabaseBackup = ({ sessionToken }) => {
  const [dbStatus, setDbStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/database/status`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setDbStatus(response.data);
    } catch (error) {
      console.error("Error fetching database status:", error);
      toast.error("Failed to fetch database status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [sessionToken]);

  const handleExport = async (includeContacts = false) => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/database/export`, {
        params: { 
          Authorization: `Bearer ${sessionToken}`,
          include_contacts: includeContacts
        },
        responseType: 'blob',
        timeout: 300000 // 5 minute timeout for full export
      });
      
      // Check if response is an error (JSON error message returned as blob)
      if (response.data.type === 'application/json' && response.data.size < 1000) {
        const text = await response.data.text();
        try {
          const errorData = JSON.parse(text);
          if (errorData.detail) {
            throw new Error(errorData.detail);
          }
        } catch (e) {
          // Not JSON error, continue with download
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `jmap_export_${includeContacts ? 'full' : 'settings'}_${new Date().toISOString().slice(0,10)}.json`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(includeContacts ? "Full database exported!" : "Settings exported successfully!");
    } catch (error) {
      console.error("Error exporting database:", error);
      // Try to read error message from blob response
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          toast.error(errorData.detail || "Failed to export database");
        } catch (e) {
          toast.error("Failed to export database");
        }
      } else {
        toast.error(error.message || "Failed to export database");
      }
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error("Please select a JSON file");
      return;
    }
    
    // Confirm before import
    if (!window.confirm("⚠️ WARNING: This will REPLACE all existing data with the imported data. Are you sure you want to continue?")) {
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/database/import`, formData, {
        params: { Authorization: `Bearer ${sessionToken}` },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      toast.success(`Database imported! ${response.data.total_imported} documents restored.`);
      
      // Refresh status
      fetchStatus();
    } catch (error) {
      console.error("Error importing database:", error);
      toast.error(error.response?.data?.detail || "Failed to import database");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="chart-container">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Database Backup & Migration
          </CardTitle>
          <CardDescription>
            Export your entire database for backup or migration to a new MongoDB cluster
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Database Status */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <h4 className="font-medium mb-3">Current Database Status</h4>
            {loading ? (
              <div className="flex items-center gap-2 text-slate-500">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Loading...
              </div>
            ) : dbStatus ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(dbStatus.collections || {}).map(([name, info]) => (
                  <div key={name} className="p-2 bg-white rounded border">
                    <div className="text-xs text-slate-500 capitalize">{name.replace(/_/g, ' ')}</div>
                    <div className="font-semibold">{info.count?.toLocaleString() || 0}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-slate-500">Unable to fetch status</div>
            )}
          </div>

          {/* Export Section */}
          <div className="p-4 border rounded-lg">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <Save className="w-4 h-4" />
              Export Database
            </h4>
            <p className="text-sm text-slate-600 mb-4">
              Export your settings, branding, users, and integration tokens. HubSpot contacts can be re-synced after migration.
            </p>
            <div className="flex gap-3">
              <Button 
                onClick={() => handleExport(false)}
                disabled={exporting}
                className="gap-2"
              >
                {exporting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Export Settings Only (Fast)
                  </>
                )}
              </Button>
              <Button 
                onClick={() => handleExport(true)}
                disabled={exporting}
                variant="outline"
                className="gap-2"
              >
                {exporting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4" />
                    Export All + Contacts (~90MB)
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Import Section */}
          <div className="p-4 border rounded-lg border-orange-200 bg-orange-50">
            <h4 className="font-medium mb-2 flex items-center gap-2 text-orange-800">
              <Upload className="w-4 h-4" />
              Import Database
            </h4>
            <p className="text-sm text-orange-700 mb-4">
              ⚠️ <strong>Warning:</strong> Importing will REPLACE all existing data. Use this when migrating to a new MongoDB cluster.
            </p>
            <div className="flex items-center gap-4">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  disabled={importing}
                  className="hidden"
                />
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  importing 
                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed' 
                    : 'bg-orange-600 text-white hover:bg-orange-700 cursor-pointer'
                }`}>
                  {importing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Select JSON File to Import
                    </>
                  )}
                </div>
              </label>
            </div>
          </div>

          {/* Import Result */}
          {importResult && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-medium text-green-800 mb-2">Import Complete</h4>
              <div className="text-sm text-green-700">
                <p>Total documents imported: <strong>{importResult.total_imported?.toLocaleString()}</strong></p>
                <p className="text-xs mt-1">From export: {importResult.original_export?.exported_at}</p>
              </div>
              {importResult.errors?.length > 0 && (
                <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                  <strong>Errors:</strong> {importResult.errors.join(', ')}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPanelPage;
